import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  TablePagination,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Menu,
  MenuList,
  Popover,
  Backdrop,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  CardGiftcard as CardIcon,
  Store as StoreIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  AttachMoney as SalesIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  Public as PublicIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/currency';
import RichTextEditor from '../components/RichTextEditor';
import AdminDailyStatisticsView from '../components/AdminDailyStatisticsView';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const drawerWidth = 240;
const drawerMobileWidth = 64;

// Helper function to preserve empty paragraphs for proper spacing
const processHtmlForDisplay = (html) => {
  if (!html) return '';
  return html
    .replace(/<p><\/p>/g, '<p>&nbsp;</p>')
    .replace(/<p><br><\/p>/g, '<p>&nbsp;</p>')
    .replace(/<p><br\/><\/p>/g, '<p>&nbsp;</p>');
};

/** Encode for query string but keep `/` literal — some CDNs/WAFs reject or mishandle `%2F` in query params. */
const encodeCrmSearchParam = (value) => encodeURIComponent(value).replace(/%2F/gi, '/');

/** CRM contact prospect scores: -1 unverified, 0–10 verified. */
const CRM_PROSPECT_SCORE_VALUES = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const crmProspectScoreFilterRestricts = (selected) => {
  if (!selected || selected.length === 0) return false;
  if (selected.length !== CRM_PROSPECT_SCORE_VALUES.length) return true;
  const set = new Set(selected);
  return !CRM_PROSPECT_SCORE_VALUES.every((s) => set.has(s));
};

const appendCrmContactsProspectScoreParams = (url, scoreFilter) => {
  if (!crmProspectScoreFilterRestricts(scoreFilter)) return url;
  scoreFilter.forEach((s) => {
    url += `&prospect_score=${encodeURIComponent(s)}`;
  });
  return url;
};

/** HSL: 0 = red, 120 = green for scores 0–10; -1 = gray. */
const crmProspectScoreBubbleSx = (score) => {
  const s = score != null && !Number.isNaN(Number(score)) ? Number(score) : -1;
  if (s === -1) {
    return { bgcolor: '#757575', color: '#fff' };
  }
  const t = Math.max(0, Math.min(1, s / 10));
  const hue = 120 * t;
  return {
    bgcolor: `hsl(${hue}, 76%, 38%)`,
    color: '#fff',
    textShadow: '0 1px 2px rgba(0,0,0,0.35)',
  };
};

const crmProspectReasonTooltipText = (contact) => {
  const reason = (contact.prospect_reason || '').trim();
  if (reason) return reason;
  return 'No verification reason saved yet. Run AI verify prospects and click Update to store one.';
};

const crmCsvEscape = (value) => {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** First valid email domain from a contact email field (semicolon-separated). */
const crmEmailDomainFromContactEmail = (rawEmail) => {
  if (!rawEmail || typeof rawEmail !== 'string') return '';
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const parts = rawEmail.split(';').map((p) => p.trim()).filter(Boolean);
  const candidates = parts.length ? parts : [rawEmail.trim()];
  for (const p of candidates) {
    if (!p.includes('@')) continue;
    if (!emailPattern.test(p)) continue;
    const domain = p.split('@')[1];
    if (domain) return domain.trim().toLowerCase();
  }
  return '';
};

const crmContactCompanyUrl = (contact, company) => {
  const www = (company?.www || '').trim();
  if (www) return www;
  return crmEmailDomainFromContactEmail(contact?.email);
};

const buildCrmContactsExportCsv = (contacts, companiesList) => {
  const header = 'First Name,Last Name,email,Job Title,Company Name,Website URL';
  const rows = contacts.map((contact) => {
    const company = companiesList.find((c) => c.id === contact.company_id);
    return [
      crmCsvEscape(contact.first_name || ''),
      crmCsvEscape(contact.last_name || ''),
      crmCsvEscape(contact.email || ''),
      crmCsvEscape(contact.title || ''),
      crmCsvEscape(company?.name || ''),
      crmCsvEscape(crmContactCompanyUrl(contact, company)),
    ].join(',');
  });
  return [header, ...rows].join('\n');
};

const downloadTextFile = (filename, text, mimeType = 'text/csv;charset=utf-8') => {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const Admin = () => {
  const { user, logout, isSuperAdmin, apiCall, loading: authLoading, API_URL, CDN_URL, token, startImpersonation, isImpersonating, originalUser, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('adminSidebarCollapsed') === 'true');
  const isNarrow = isMobile || sidebarCollapsed;
  const currentDrawerWidth = isNarrow ? drawerMobileWidth : drawerWidth;
  const [selectedTab, setSelectedTab] = useState('users');

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('adminSidebarCollapsed', String(next));
      return next;
    });
  };
  const [adminTimezone, setAdminTimezone] = useState(() => {
    return localStorage.getItem('adminTimezone') || 'UTC';
  });

  // Helper function to format dates with admin timezone
  const formatDateWithTimezone = (date) => {
    if (!date) return '';
    return dayjs.utc(date).tz(adminTimezone).format('MM/DD/YYYY, h:mm:ss A');
  };

  const formatDateShort = (date) => {
    if (!date) return '';
    return dayjs.utc(date).tz(adminTimezone).format('M/D/YYYY');
  };

  // Parse semicolon-separated email field: trim, validate, dedupe. Returns array of valid emails.
  const parseEmailField = (rawEmail) => {
    if (!rawEmail || typeof rawEmail !== 'string') return [];
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const parts = rawEmail.split(';').map(p => p.trim()).filter(Boolean);
    const seen = new Set();
    return parts.filter(p => {
      if (!emailPattern.test(p)) return false;
      const lower = p.toLowerCase();
      if (seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });
  };

  // Helper function to add contacts to SEND recipients
  const addContactsToSend = async (contacts) => {
    if (!contacts || contacts.length === 0) return;

    const existingEmails = new Set(salesRecipients.map(r => r.email?.toLowerCase()));
    const newRecipients = [];
    let duplicateCount = 0;

    for (const contact of contacts) {
      const emails = parseEmailField(contact.email);
      if (emails.length === 0) continue;

      const company = crmCompaniesList.find(c => c.id === contact.company_id);
      const baseRecipient = {
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        company_name: company?.name || '',
        title: contact.title || ''
      };

      for (const email of emails) {
        const emailLower = email.toLowerCase();
        if (existingEmails.has(emailLower)) {
          duplicateCount++;
          continue;
        }
        existingEmails.add(emailLower);
        newRecipients.push({ ...baseRecipient, email });
      }
    }

    if (newRecipients.length > 0) {
      setSalesRecipients(prev => [...prev, ...newRecipients]);
      if (duplicateCount > 0) {
        setSuccess(`Added ${newRecipients.length} contacts to SEND list. ${duplicateCount} duplicate(s) skipped.`);
      } else {
        setSuccess(`Added ${newRecipients.length} contacts to SEND list.`);
      }
    } else if (duplicateCount > 0) {
      setError(`All ${duplicateCount} selected contact(s) are already in the SEND list.`);
    }
  };

  // Handle adding selected companies to SEND
  const handleAddCompaniesToSend = async () => {
    if (selectedCrmCompanies.size === 0) return;

    setLoading(true);
    try {
      // Fetch contacts for all selected companies
      const companyIds = Array.from(selectedCrmCompanies);
      const allContacts = [];

      for (const companyId of companyIds) {
        const result = await apiCall('get', `/api/sales/crm/contacts?company_id=${companyId}&per_page=1000`);
        if (result.success && result.data) {
          allContacts.push(...(result.data.contacts || []));
        }
      }

      await addContactsToSend(allContacts);
      setSelectedCrmCompanies(new Set());
    } catch (error) {
      setError('Failed to fetch contacts for selected companies');
    } finally {
      setLoading(false);
    }
  };

  // Handle viewing company contacts - switch to contacts tab with company filter
  const handleViewCompanyContacts = (companyId) => {
    setCrmTab('contacts');
    setCrmCompanyFilter(companyId);
    setCrmContactsPage(0); // Reset to first page
    setCrmSearchFilter(''); // Clear search input when viewing a company
  };

  // Handle adding selected contacts to SEND
  const handleAddContactsToSend = () => {
    if (selectedCrmContacts.size === 0) return;

    const selectedContacts = crmContacts.filter(contact => selectedCrmContacts.has(contact.id));
    addContactsToSend(selectedContacts);
    setSelectedCrmContacts(new Set());
  };

  const handleInlineStatusChange = async (contactId, newStatus) => {
    if (!contactId) return;
    setStatusMenuAnchor(null);
    setStatusMenuContactId(null);
    setStatusUpdatingContactId(contactId);
    try {
      const result = await apiCall('put', `/api/sales/crm/contacts/${contactId}`, { status: newStatus });
      if (result.success && result.data) {
        if (newStatus === 'priority') {
          // Update the specific contact and also update other contacts from the same company (except wrong/lost)
          const targetContact = crmContacts.find(c => c.id === contactId);
          const companyId = targetContact?.company_id || result.data.company_id;
          setCrmContacts(prev => prev.map(c => {
            if (c.id === contactId) return { ...c, status: result.data.status ?? newStatus };
            if (c.company_id === companyId && c.status !== 'wrong' && c.status !== 'lost') return { ...c, status: 'priority' };
            return c;
          }));
        } else {
          setCrmContacts(prev => prev.map(c => c.id === contactId ? { ...c, status: result.data.status ?? newStatus } : c));
        }
      } else {
        setError(result.error || 'Failed to update contact status');
      }
    } catch (err) {
      setError('An error occurred while updating contact status');
      console.error('Error updating contact status:', err);
    } finally {
      setStatusUpdatingContactId(null);
    }
  };

  const handleQuickEditNotesSave = async (contactId) => {
    if (!contactId) return;
    if (notesQuickEditValue === notesQuickEditOriginalValue) {
      setNotesQuickEditAnchor(null);
      setNotesQuickEditContactId(null);
      return;
    }
    setNotesUpdatingContactId(contactId);
    try {
      const result = await apiCall('put', `/api/sales/crm/contacts/${contactId}`, { notes: notesQuickEditValue });
      if (result.success && result.data) {
        setCrmContacts(prev => prev.map(c => c.id === contactId ? { ...c, notes: result.data.notes ?? notesQuickEditValue } : c));
        setNotesQuickEditAnchor(null);
        setNotesQuickEditContactId(null);
      } else {
        setError(result.error || 'Failed to update contact notes');
      }
    } catch (err) {
      setError('An error occurred while updating contact notes');
      console.error('Error updating contact notes:', err);
    } finally {
      setNotesUpdatingContactId(null);
    }
  };

  const handleInlineCompanyStatusChange = async (companyId, newStatus) => {
    if (!companyId) return;
    setCompanyStatusMenuAnchor(null);
    setCompanyStatusMenuCompanyId(null);
    setCompanyStatusUpdatingId(companyId);
    try {
      const result = await apiCall('put', `/api/sales/crm/companies/${companyId}`, { status: newStatus });
      if (result.success && result.data) {
        setCrmCompanies(prev => prev.map(c => c.id === companyId ? { ...c, status: result.data.status ?? newStatus } : c));
      } else {
        setError(result.error || 'Failed to update company status');
      }
    } catch (err) {
      setError('An error occurred while updating company status');
      console.error('Error updating company status:', err);
    } finally {
      setCompanyStatusUpdatingId(null);
    }
  };

  const openCrmCompanyEdit = (company) => {
    if (!company) return;
    setEditingCrmCompany(company);
    setCrmCompanyFormData({
      name: company.name || '',
      www: company.www || '',
      linkedin: company.linkedin || '',
      email: company.email || '',
      phone: company.phone || '',
      industry: company.industry || '',
      time_zone: company.time_zone || '',
      status: company.status || 'none'
    });
    setCrmCompanyDialogOpen(true);
  };

  const handleUpdateStatusFromCommunication = async (communicationId) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await apiCall('post', `/api/sales/crm/contacts/update-status/${communicationId}`);

      if (result.success && result.data) {
        const wrongCount = result.data.updated_wrong_count || 0;
        const openCount = result.data.updated_open_count || 0;
        setSuccess(`Successfully updated ${wrongCount} contact(s) to WRONG status and ${openCount} contact(s) to OPEN status`);

        // Reload the communication details to reflect any changes
        if (selectedComm && selectedComm.id === communicationId) {
          const reloadResult = await apiCall('get', `/api/sales/communication/${communicationId}`);
          if (reloadResult.success) {
            setSelectedComm(reloadResult.data);
          }
        }
      } else {
        setError(result.error || 'Failed to update contact statuses');
      }
    } catch (err) {
      setError('An error occurred while updating contact statuses');
      console.error('Error updating contact statuses:', err);
    } finally {
      setLoading(false);
    }
  };
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [companies, setCompanies] = useState([]);
  const [rewardProviders, setRewardProviders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [dialogError, setDialogError] = useState('');
  const [filters, setFilters] = useState({
    company_id: '',
    role: '',
    search: ''
  });
  const [companyFilters, setCompanyFilters] = useState({
    country: '',
    tier: '',
    search: ''
  });
  const [rewardFilters, setRewardFilters] = useState({
    provider_id: '',
    company_id: '',
    search: ''
  });
  const [logFilters, setLogFilters] = useState({
    log_type: '',
    log_type_negative: false,
    company_id: '',
    search: ''
  });
  const [companyRewardProviders, setCompanyRewardProviders] = useState([]);
  const [availableRewardProviders, setAvailableRewardProviders] = useState([]);
  const [loadingRewardProviders, setLoadingRewardProviders] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configJson, setConfigJson] = useState('');
  const [configError, setConfigError] = useState('');
  
  // Extra Modules state
  const [extraModules, setExtraModules] = useState([]);
  const [companyExtraModules, setCompanyExtraModules] = useState([]);
  const [loadingExtraModules, setLoadingExtraModules] = useState(false);
  const [extraModuleConfigDialogOpen, setExtraModuleConfigDialogOpen] = useState(false);
  const [editingExtraModuleConfig, setEditingExtraModuleConfig] = useState(null);
  const [extraModuleConfigJson, setExtraModuleConfigJson] = useState('');
  const [extraModuleConfigError, setExtraModuleConfigError] = useState('');

  const [tremendousSetupLoading, setTremendousSetupLoading] = useState(false);
  const [tremendousBalance, setTremendousBalance] = useState(null);
  const [tremendousBalanceLoading, setTremendousBalanceLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
  const [bulkDeleteConfirmed, setBulkDeleteConfirmed] = useState(false);
  const [selectedDealerIds, setSelectedDealerIds] = useState([]);
  const [openDeleteDealerDialog, setOpenDeleteDealerDialog] = useState(false);
  const [deletingDealerId, setDeletingDealerId] = useState(null);
  const [deleteDealerConfirmed, setDeleteDealerConfirmed] = useState(false);
  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteUserConfirmed, setDeleteUserConfirmed] = useState(false);
  const [userFormDealers, setUserFormDealers] = useState([]);
  const [loadingUserFormDealers, setLoadingUserFormDealers] = useState(false);

  // Sales section state
  const [salesTab, setSalesTab] = useState('history');
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesStatistics, setSalesStatistics] = useState(null);
  const [salesSettings, setSalesSettings] = useState(null);
  const [salesRecipients, setSalesRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedSalesComms, setSelectedSalesComms] = useState([]);
  const [deleteCommDialogOpen, setDeleteCommDialogOpen] = useState(false);
  const [deleteBulkDialogOpen, setDeleteBulkDialogOpen] = useState(false);
  const [commToDelete, setCommToDelete] = useState(null);
  const [salesFormData, setSalesFormData] = useState({
    title: '',
    subtitle: '',
    content: '',
    extra_content: '',
    custom_cta_title: '',
    custom_cta_url: '',
    test_email: '',
    email_type: 'template_no_cta',  // 'template_no_cta' or 'plain_text'
    send_status: 'email'     // Status to set for recipients when SEND NOW (email, email_2, email_3)
  });
  const [salesPage, setSalesPage] = useState(0);
  const [salesRowsPerPage, setSalesRowsPerPage] = useState(25);
  const [salesTotal, setSalesTotal] = useState(0);
  const [salesSortBy, setSalesSortBy] = useState('created');
  const [salesSortOrder, setSalesSortOrder] = useState('desc');
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [viewCommDialogOpen, setViewCommDialogOpen] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [recipientsSortBy, setRecipientsSortBy] = useState('modified');
  const [recipientsSortOrder, setRecipientsSortOrder] = useState('desc');

  // CRM section state
  const [crmTab, setCrmTab] = useState('companies');
  const [crmCompanies, setCrmCompanies] = useState([]);
  const [crmContacts, setCrmContacts] = useState([]);
  const [crmContactsStatistics, setCrmContactsStatistics] = useState(null);
  const [loadingCrmStatistics, setLoadingCrmStatistics] = useState(false);
  const [crmCompaniesPage, setCrmCompaniesPage] = useState(0);
  const [crmCompaniesRowsPerPage, setCrmCompaniesRowsPerPage] = useState(25);
  const [crmCompaniesTotal, setCrmCompaniesTotal] = useState(0);
  const [crmContactsPage, setCrmContactsPage] = useState(0);
  const [kasprCredits, setKasprCredits] = useState(null);
  const [kasprCreditsDialogOpen, setKasprCreditsDialogOpen] = useState(false);

  const handleCheckKasprCredits = async () => {
    setLoading(true);
    const result = await apiCall('get', '/api/sales/crm/kaspr-credits');
    setLoading(false);
    if (result.success && result.data) {
      setKasprCredits(result.data);
      setKasprCreditsDialogOpen(true);
      setSuccess('Kaspr credits fetched successfully');
    } else {
      setError(result.error || 'Failed to fetch Kaspr credits');
    }
  };

  const handleLinkedinImport = async () => {
    const lines = linkedinImportText.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    setError('');
    setSuccess('');
    setWarning('');
    setLinkedinImportLoading(true);
    const result = await apiCall('post', '/api/sales/crm/contacts/import-linkedin', { urls: lines });
    setLinkedinImportLoading(false);
    if (result.success && result.data) {
      setLinkedinImportResult(result.data);
      const d = result.data;
      const created = d.created ?? 0;
      const failed = d.failed ?? 0;
      const invalid = d.invalid ?? 0;
      const skipLi = d.skipped_duplicate_linkedin ?? 0;
      const skipEmail = d.skipped_duplicate_email ?? 0;
      const newCo = d.companies_created ?? 0;
      const firstErr = d.errors?.[0]?.detail;

      if (created === 0 && failed > 0) {
        setError(
          `LinkedIn import: no new contacts. ${failed} failed (often Kaspr: suspended workspace, no credits, or bad API key).${firstErr ? ` Example: ${firstErr}` : ''} See the dialog for each URL.`
        );
      } else if (created === 0 && (skipLi > 0 || skipEmail > 0 || invalid > 0)) {
        setWarning(
          `Nothing new imported: ${skipLi} already in CRM (LinkedIn), ${skipEmail} skipped (email exists), ${invalid} invalid lines.${failed ? ` ${failed} Kaspr/API failures.` : ''} Details are in the dialog.`
        );
      } else if (created > 0 && (failed > 0 || invalid > 0 || skipLi > 0 || skipEmail > 0)) {
        setSuccess(
          `Imported ${created} contact(s)${newCo ? `, ${newCo} new compan${newCo === 1 ? 'y' : 'ies'}` : ''}.`
        );
        setWarning(
          `Some rows were not added: ${failed} failed, ${invalid} invalid, ${skipLi} duplicate LinkedIn, ${skipEmail} duplicate email. Open the dialog for full detail.`
        );
      } else {
        setSuccess(
          `Imported ${created} contact(s)${newCo ? ` and ${newCo} new compan${newCo === 1 ? 'y' : 'ies'}` : ''}.`
        );
      }

      apiCall('get', '/api/sales/crm/companies?page=1&per_page=1000').then((r) => {
        if (r.success && r.data) {
          setCrmCompaniesList(r.data.companies || []);
        }
      });
    } else {
      setError(result.error || 'LinkedIn import request failed (network or server error).');
    }
  };
  const [crmContactsRowsPerPage, setCrmContactsRowsPerPage] = useState(25);
  const [crmContactsTotal, setCrmContactsTotal] = useState(0);
  const [crmCompanyFilter, setCrmCompanyFilter] = useState('');
  const [crmStatusFilter, setCrmStatusFilter] = useState('');
  const [crmContactsStatusFilter, setCrmContactsStatusFilter] = useState([]);
  const [crmSearchFilter, setCrmSearchFilter] = useState('');
  const [crmPhoneFilter, setCrmPhoneFilter] = useState(false);
  const [crmNotesFilter, setCrmNotesFilter] = useState(false);
  const [crmContactsScoreFilter, setCrmContactsScoreFilter] = useState(() => [...CRM_PROSPECT_SCORE_VALUES]);
  const [crmCompaniesPhoneFilter, setCrmCompaniesPhoneFilter] = useState(false);
  const [crmCompaniesSortBy, setCrmCompaniesSortBy] = useState('name');
  const [crmCompaniesSortOrder, setCrmCompaniesSortOrder] = useState('asc');
  const [crmContactsSortBy, setCrmContactsSortBy] = useState('company_id');
  const [crmContactsSortOrder, setCrmContactsSortOrder] = useState('asc');
  const [crmCompanyDialogOpen, setCrmCompanyDialogOpen] = useState(false);
  const [crmContactDialogOpen, setCrmContactDialogOpen] = useState(false);
  const [savingCrmContact, setSavingCrmContact] = useState(false);
  const [fetchingPhoneContactIds, setFetchingPhoneContactIds] = useState(new Set());
  const [editingCrmCompany, setEditingCrmCompany] = useState(null);
  const [editingCrmContact, setEditingCrmContact] = useState(null);
  const [inlineEditingCrmContactId, setInlineEditingCrmContactId] = useState(null);
  const [crmCompanyFormData, setCrmCompanyFormData] = useState({
    name: '',
    www: '',
    linkedin: '',
    email: '',
    phone: '',
    industry: '',
    time_zone: '',
    status: 'none'
  });
  const [crmContactFormData, setCrmContactFormData] = useState({
    company_id: '',
    first_name: '',
    last_name: '',
    title: '',
    linkedin: '',
    email: '',
    phone: '',
    notes: '',
    prospect_score: -1,
    prospect_reason: '',
    status: 'none'
  });
  const [crmCompaniesList, setCrmCompaniesList] = useState([]); // For contact form dropdown
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuContactId, setStatusMenuContactId] = useState(null);
  const [statusUpdatingContactId, setStatusUpdatingContactId] = useState(null);
  const [notesQuickEditAnchor, setNotesQuickEditAnchor] = useState(null);
  const [notesQuickEditContactId, setNotesQuickEditContactId] = useState(null);
  const [notesQuickEditValue, setNotesQuickEditValue] = useState('');
  const [notesQuickEditOriginalValue, setNotesQuickEditOriginalValue] = useState('');
  const [notesUpdatingContactId, setNotesUpdatingContactId] = useState(null);
  const [companyStatusMenuAnchor, setCompanyStatusMenuAnchor] = useState(null);
  const [companyStatusMenuCompanyId, setCompanyStatusMenuCompanyId] = useState(null);
  const [companyStatusUpdatingId, setCompanyStatusUpdatingId] = useState(null);

  // CRM selection state
  const [selectedCrmCompanies, setSelectedCrmCompanies] = useState(new Set());
  const [selectedCrmContacts, setSelectedCrmContacts] = useState(new Set());
  const [crmCompanyMenuAnchor, setCrmCompanyMenuAnchor] = useState(null);
  const [crmContactMenuAnchor, setCrmContactMenuAnchor] = useState(null);
  const [linkedinImportOpen, setLinkedinImportOpen] = useState(false);
  const [linkedinImportText, setLinkedinImportText] = useState('');
  const [linkedinImportLoading, setLinkedinImportLoading] = useState(false);
  const [linkedinImportResult, setLinkedinImportResult] = useState(null);
  const [crmContactsIdFilter, setCrmContactsIdFilter] = useState(null);
  const [aiVerifyOpen, setAiVerifyOpen] = useState(false);
  const [aiVerifyLoading, setAiVerifyLoading] = useState(false);
  const [aiVerifyApplyLoading, setAiVerifyApplyLoading] = useState(false);
  const [aiVerifyResults, setAiVerifyResults] = useState([]);
  const [crmContactsRefreshTick, setCrmContactsRefreshTick] = useState(0);
  const [crmContactsCsvExporting, setCrmContactsCsvExporting] = useState(false);

  const handleExportCrmContactsCsv = async () => {
    if (selectedCrmContacts.size === 0) return;
    setError('');
    setSuccess('');
    setCrmContactsCsvExporting(true);
    try {
      const contactIds = Array.from(selectedCrmContacts);
      const perPage = Math.min(contactIds.length, 1000);
      let url = `/api/sales/crm/contacts?page=1&per_page=${perPage}`;
      contactIds.forEach((id) => {
        url += `&contact_ids=${encodeURIComponent(id)}`;
      });
      const result = await apiCall('get', url);
      if (!result.success || !result.data) {
        setError(result.error || 'Failed to fetch contacts for export');
        return;
      }
      const contacts = result.data.contacts || [];
      if (contacts.length === 0) {
        setError('No contacts found to export');
        return;
      }
      const csv = buildCrmContactsExportCsv(contacts, crmCompaniesList);
      const filename = `crm-contacts-${dayjs().format('YYYY-MM-DD')}.csv`;
      downloadTextFile(filename, csv);
      setSuccess(`Exported ${contacts.length} contact(s).`);
    } finally {
      setCrmContactsCsvExporting(false);
    }
  };

  const handleAiVerifyProspects = async () => {
    if (selectedCrmContacts.size === 0) return;
    setError('');
    setSuccess('');
    setAiVerifyLoading(true);
    try {
      const contact_ids = Array.from(selectedCrmContacts);
      const result = await apiCall('post', '/api/sales/crm/contacts/verify-prospects', { contact_ids });
      if (result.success && result.data) {
        setAiVerifyResults(result.data.results || []);
        setAiVerifyOpen(true);
      } else {
        setError(result.error || 'AI verification failed');
      }
    } finally {
      setAiVerifyLoading(false);
    }
  };

  const handleAiVerifyApply = async () => {
    if (!aiVerifyResults.length) {
      setAiVerifyOpen(false);
      return;
    }
    setAiVerifyApplyLoading(true);
    setError('');
    const updates = aiVerifyResults.map((r) => ({
      contact_id: r.contact_id,
      prospect_score: r.prospect_score,
      prospect_reason: r.reason ?? '',
    }));
    const result = await apiCall('post', '/api/sales/crm/contacts/apply-prospect-scores', { updates });
    setAiVerifyApplyLoading(false);
    if (result.success && result.data) {
      setAiVerifyOpen(false);
      setAiVerifyResults([]);
      setSelectedCrmContacts(new Set());
      setCrmContactsRefreshTick((t) => t + 1);
      setSuccess(`Updated prospect scores for ${result.data.updated} contact(s).`);
    } else {
      setError(result.error || 'Failed to save prospect scores');
    }
  };

  useEffect(() => {
    // Wait for auth to load before checking access
    if (authLoading) return;

    // Check if user is authenticated first
    if (!user || !token) {
      navigate('/login');
      return;
    }

    if (user.role !== 'SuperAdmin' && user.role !== 'Sales') {
      navigate('/');
      return;
    }

    if (user.role === 'Sales') {
      const allowed = ['sales', 'settings'];
      if (!allowed.includes(selectedTab)) {
        setSelectedTab('sales');
        return;
      }
    }

    // Clear selected users and dealers when switching tabs or filters change
    setSelectedUserIds([]);
    setSelectedDealerIds([]);

    const dataTabs = ['users', 'companies', 'dealers', 'rewards', 'logs'];
    if (!dataTabs.includes(selectedTab)) {
      return;
    }

    loadData();
  }, [selectedTab, page, rowsPerPage, filters, companyFilters, rewardFilters, logFilters, authLoading, navigate, user, token]);

  useEffect(() => {
    if (authLoading || !user || selectedTab !== 'statistics') return;
    if (companies.length > 0) return;

    const loadCompaniesForStatistics = async () => {
      const result = await apiCall('get', '/api/companies?page=1&per_page=1000');
      if (result.success) {
        setCompanies(result.data.companies || []);
      }
    };

    loadCompaniesForStatistics();
  }, [selectedTab, authLoading, user, companies.length, apiCall]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tremendousSuccess = urlParams.get('tremendous_success');
    const companyId = urlParams.get('company_id');

    if (tremendousSuccess === 'true' && companyId) {
      setSuccess('Tremendous OAuth connection completed successfully!');
      loadCompanyRewardProviders(parseInt(companyId));
      window.history.replaceState({}, '', '/admin');
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    if (user.role === 'Sales') return;
    loadCompanies();
    loadRewardProviders();
    loadExtraModules();
  }, [authLoading, user?.role]);

  useEffect(() => {
    if (!openDialog || selectedTab !== 'users') {
      setUserFormDealers([]);
      setLoadingUserFormDealers(false);
      return;
    }
    if (formData.role !== 'Dealer' || formData.company_id === '' || formData.company_id == null) {
      setUserFormDealers([]);
      setLoadingUserFormDealers(false);
      return;
    }
    const cid = typeof formData.company_id === 'string'
      ? parseInt(formData.company_id, 10)
      : formData.company_id;
    if (!cid || Number.isNaN(cid)) {
      setUserFormDealers([]);
      setLoadingUserFormDealers(false);
      return;
    }
    let cancelled = false;
    const loadDealersForUserForm = async () => {
      setLoadingUserFormDealers(true);
      const result = await apiCall('get', `/api/dealers?company_id=${cid}&per_page=500&page=1`);
      if (!cancelled) {
        if (result.success && result.data?.dealers) {
          setUserFormDealers(result.data.dealers);
        } else {
          setUserFormDealers([]);
        }
        setLoadingUserFormDealers(false);
      }
    };
    loadDealersForUserForm();
    return () => { cancelled = true; };
  }, [openDialog, selectedTab, formData.role, formData.company_id]);

  useEffect(() => {
    if (!openDialog || selectedTab !== 'users' || formData.role !== 'Dealer' || loadingUserFormDealers) return;
    if (!userFormDealers.length || formData.dealer_id == null || formData.dealer_id === '') return;
    const id = Number(formData.dealer_id);
    if (Number.isNaN(id)) return;
    if (!userFormDealers.some((d) => d.id === id)) {
      setFormData((prev) => ({ ...prev, dealer_id: undefined }));
    }
  }, [openDialog, selectedTab, formData.role, formData.dealer_id, userFormDealers, loadingUserFormDealers]);

  // Load CRM companies list for dropdowns
  useEffect(() => {
    const loadCrmCompaniesList = async () => {
      const result = await apiCall('get', `/api/sales/crm/companies?page=1&per_page=1000`);
      if (result.success && result.data) {
        setCrmCompaniesList(result.data.companies || []);
      }
    };
    if (selectedTab === 'sales' && salesTab === 'crm') {
      loadCrmCompaniesList();
    }
  }, [selectedTab, salesTab]);

  // Refresh companies list when opening Add Contact dialog or inline edit (so dropdown shows newly added companies)
  useEffect(() => {
    if ((crmContactDialogOpen || inlineEditingCrmContactId) && selectedTab === 'sales' && salesTab === 'crm') {
      apiCall('get', '/api/sales/crm/companies?page=1&per_page=1000').then((result) => {
        if (result.success && result.data) {
          setCrmCompaniesList(result.data.companies || []);
        }
      });
    }
  }, [crmContactDialogOpen, inlineEditingCrmContactId]);

  useEffect(() => {
    setInlineEditingCrmContactId(null);
    setEditingCrmContact(null);
  }, [crmContactsPage, crmContactsRowsPerPage, crmContactsSortBy, crmContactsSortOrder, crmCompanyFilter, crmContactsStatusFilter, crmSearchFilter, crmPhoneFilter, crmNotesFilter, crmContactsScoreFilter]);

  const getCrmContactFormDataFromContact = (contact) => ({
    company_id: contact.company_id || '',
    first_name: contact.first_name || '',
    last_name: contact.last_name || '',
    title: contact.title || '',
    linkedin: contact.linkedin || '',
    email: contact.email || '',
    phone: contact.phone || '',
    notes: contact.notes || '',
    prospect_score: contact.prospect_score != null ? contact.prospect_score : -1,
    prospect_reason: contact.prospect_reason || '',
    status: contact.status || 'none',
  });

  const populateCrmContactForm = (contact) => {
    setEditingCrmContact(contact);
    setCrmContactFormData(getCrmContactFormDataFromContact(contact));
  };

  const closeInlineCrmContactEdit = () => {
    setInlineEditingCrmContactId(null);
    setEditingCrmContact(null);
  };

  const toggleInlineCrmContactEdit = (contact) => {
    if (inlineEditingCrmContactId === contact.id) {
      closeInlineCrmContactEdit();
    } else {
      populateCrmContactForm(contact);
      setInlineEditingCrmContactId(contact.id);
    }
  };

  const buildCrmContactsReloadUrl = () => {
    let reloadUrl = `/api/sales/crm/contacts?page=${crmContactsPage + 1}&per_page=${crmContactsRowsPerPage}`;
    if (crmCompanyFilter) reloadUrl += `&company_id=${crmCompanyFilter}`;
    crmContactsStatusFilter.forEach((s) => { reloadUrl += `&status_filter=${encodeURIComponent(s)}`; });
    if (crmSearchFilter) reloadUrl += `&search=${encodeCrmSearchParam(crmSearchFilter)}`;
    if (crmPhoneFilter) reloadUrl += `&has_phone=true`;
    if (crmNotesFilter) reloadUrl += `&has_notes=true`;
    reloadUrl = appendCrmContactsProspectScoreParams(reloadUrl, crmContactsScoreFilter);
    reloadUrl += `&sort_by=${crmContactsSortBy}&sort_order=${crmContactsSortOrder}`;
    return reloadUrl;
  };

  const handleSaveCrmContact = async ({ isCreate, onSuccess }) => {
    if (!crmContactFormData.company_id) {
      setError('Company is required');
      return;
    }
    const prospectScore = Number(crmContactFormData.prospect_score);
    if (Number.isNaN(prospectScore) || prospectScore < -1 || prospectScore > 10) {
      setError('Score must be between -1 and 10');
      return;
    }
    setSavingCrmContact(true);
    try {
      const method = isCreate ? 'post' : 'put';
      const url = isCreate
        ? '/api/sales/crm/contacts'
        : `/api/sales/crm/contacts/${editingCrmContact.id}`;
      const formData = {
        ...crmContactFormData,
        company_id: parseInt(crmContactFormData.company_id, 10),
        prospect_score: prospectScore,
      };
      const result = await apiCall(method, url, formData);
      if (result.success) {
        setSuccess(isCreate ? 'Contact created' : 'Contact updated');
        if (isCreate) {
          const reloadResult = await apiCall('get', buildCrmContactsReloadUrl());
          if (reloadResult.success && reloadResult.data) {
            setCrmContacts(reloadResult.data.contacts || []);
            setCrmContactsTotal(reloadResult.data.total || 0);
          }
        } else if (result.data.status === 'priority') {
          setCrmContacts(prev => prev.map(c => {
            if (c.id === result.data.id) return result.data;
            if (c.company_id === result.data.company_id && c.status !== 'wrong' && c.status !== 'lost') {
              return { ...c, status: 'priority' };
            }
            return c;
          }));
        } else {
          setCrmContacts(prev => prev.map(c => c.id === result.data.id ? result.data : c));
        }
        onSuccess?.();
      } else {
        setError(result.error || 'Failed to save contact');
      }
    } finally {
      setSavingCrmContact(false);
    }
  };

  // Load CRM data when CRM tab is selected
  useEffect(() => {
    if (selectedTab !== 'sales' || salesTab !== 'crm') return;

    const loadCrmData = async () => {
      setLoading(true);

      if (crmTab === 'companies') {
        let url = `/api/sales/crm/companies?page=${crmCompaniesPage + 1}&per_page=${crmCompaniesRowsPerPage}`;
        if (crmStatusFilter) url += `&status_filter=${encodeURIComponent(crmStatusFilter)}`;
        if (crmSearchFilter) url += `&search=${encodeCrmSearchParam(crmSearchFilter)}`;
        if (crmCompaniesPhoneFilter) url += `&has_phone=true`;
        url += `&sort_by=${crmCompaniesSortBy}&sort_order=${crmCompaniesSortOrder}`;
        console.log('CRM Companies - Status filter:', crmStatusFilter, 'URL:', url);
        const result = await apiCall('get', url);
        console.log('CRM Companies - Response:', result.success, 'Count:', result.data?.companies?.length || 0, 'Total:', result.data?.total || 0);
        if (result.success && result.data) {
          setCrmCompanies(result.data.companies || []);
          setCrmCompaniesTotal(result.data.total || 0);
        }
      } else if (crmTab === 'contacts') {
        let url = `/api/sales/crm/contacts?page=${crmContactsPage + 1}&per_page=${crmContactsRowsPerPage}`;
        if (crmCompanyFilter) url += `&company_id=${crmCompanyFilter}`;
        crmContactsStatusFilter.forEach((s) => { url += `&status_filter=${encodeURIComponent(s)}`; });
        if (crmSearchFilter) url += `&search=${encodeCrmSearchParam(crmSearchFilter)}`;
        if (crmPhoneFilter) url += `&has_phone=true`;
        if (crmNotesFilter) url += `&has_notes=true`;
        url = appendCrmContactsProspectScoreParams(url, crmContactsScoreFilter);
        if (crmContactsIdFilter && crmContactsIdFilter.length > 0) {
          crmContactsIdFilter.forEach((id) => {
            url += `&contact_ids=${id}`;
          });
        }
        url += `&sort_by=${crmContactsSortBy}&sort_order=${crmContactsSortOrder}`;
        console.log('CRM Contacts - Status filter:', crmContactsStatusFilter, 'URL:', url);
        const result = await apiCall('get', url);
        console.log('CRM Contacts - Response:', result.success, 'Count:', result.data?.contacts?.length || 0, 'Total:', result.data?.total || 0);
        if (result.success && result.data) {
          setCrmContacts(result.data.contacts || []);
          setCrmContactsTotal(result.data.total || 0);
        }
      }

      setLoading(false);
    };

    loadCrmData();
  }, [selectedTab, salesTab, crmTab, crmCompaniesPage, crmCompaniesRowsPerPage, crmContactsPage, crmContactsRowsPerPage, crmCompanyFilter, crmStatusFilter, crmContactsStatusFilter, crmSearchFilter, crmPhoneFilter, crmNotesFilter, crmContactsScoreFilter, crmCompaniesPhoneFilter, crmCompaniesSortBy, crmCompaniesSortOrder, crmContactsSortBy, crmContactsSortOrder, crmContactsIdFilter, crmContactsRefreshTick]);

  // Load CRM contacts statistics when Statistics tab is selected
  useEffect(() => {
    if (selectedTab !== 'sales' || salesTab !== 'crm' || crmTab !== 'statistics') return;

    const loadCrmStatistics = async () => {
      setLoadingCrmStatistics(true);
      const result = await apiCall('get', '/api/sales/crm/contacts/statistics');
      if (result.success && result.data) {
        setCrmContactsStatistics(result.data);
      }
      setLoadingCrmStatistics(false);
    };

    loadCrmStatistics();
  }, [selectedTab, salesTab, crmTab]);

  // Load Sales data when sales tab is selected
  useEffect(() => {
    if (selectedTab !== 'sales') return;

    const loadSalesData = async () => {
      setLoading(true);

      if (salesTab === 'history') {
        const result = await apiCall('get', `/api/sales/history?page=${salesPage + 1}&per_page=${salesRowsPerPage}&sort_by=${salesSortBy}&sort_order=${salesSortOrder}`);
        if (result.success) {
          setSalesHistory(result.data.communications || []);
          setSalesTotal(result.data.total || 0);
        }
        // Clear selections when loading history
        setSelectedSalesComms([]);
      } else if (salesTab === 'statistics') {
        const result = await apiCall('get', '/api/sales/statistics');
        if (result.success) {
          setSalesStatistics(result.data);
        }
      } else if (salesTab === 'settings') {
        const result = await apiCall('get', '/api/sales/settings');
        if (result.success) {
          setSalesSettings(result.data);
        }
      } else if (salesTab === 'send') {
        // Load settings to populate default CTA values when opening send tab
        const result = await apiCall('get', '/api/sales/settings');
        if (result.success) {
          const settings = result.data;
          // Populate CTA fields from settings if they're empty
          setSalesFormData(prev => ({
            ...prev,
            custom_cta_title: prev.custom_cta_title || settings.default_cta_title || '',
            custom_cta_url: prev.custom_cta_url || settings.default_cta_url || ''
          }));
        }
      }

      setLoading(false);
    };

    loadSalesData();
  }, [selectedTab, salesTab, salesPage, salesRowsPerPage, salesSortBy, salesSortOrder]);

  const handleSalesSort = (property) => {
    const isAsc = salesSortBy === property && salesSortOrder === 'asc';
    setSalesSortOrder(isAsc ? 'desc' : 'asc');
    setSalesSortBy(property);
    setSalesPage(0); // Reset to first page when sorting changes
  };

  const handleRecipientsSort = (property) => {
    const isAsc = recipientsSortBy === property && recipientsSortOrder === 'asc';
    setRecipientsSortOrder(isAsc ? 'desc' : 'asc');
    setRecipientsSortBy(property);
  };

  const loadData = async () => {
    setLoading(true);
    setError('');

    let endpoint;
    const baseParams = `page=${page + 1}&per_page=${rowsPerPage}`;

    switch (selectedTab) {
      case 'users':
        let userParams = baseParams;
        if (filters.company_id) userParams += `&company_id=${filters.company_id}`;
        if (filters.role) userParams += `&role=${filters.role}`;
        if (filters.search) userParams += `&search=${encodeURIComponent(filters.search)}`;
        endpoint = `/api/users?${userParams}`;
        break;
      case 'companies':
        let companyParams = baseParams;
        if (companyFilters.country) companyParams += `&country=${encodeURIComponent(companyFilters.country)}`;
        if (companyFilters.tier) companyParams += `&tier=${encodeURIComponent(companyFilters.tier)}`;
        if (companyFilters.search) companyParams += `&search=${encodeURIComponent(companyFilters.search)}`;
        endpoint = `/api/companies?${companyParams}`;
        break;
      case 'dealers':
        let dealerParams = baseParams;
        if (filters.search) dealerParams += `&search=${encodeURIComponent(filters.search)}`;
        if (filters.company_id) dealerParams += `&company_id=${filters.company_id}`;
        endpoint = `/api/dealers?${dealerParams}`;
        break;
      case 'rewards':
        let rewardParams = baseParams;
        if (rewardFilters.provider_id) rewardParams += `&provider_id=${rewardFilters.provider_id}`;
        if (rewardFilters.company_id) {
          rewardParams += `&company_id=${rewardFilters.company_id}&include_global_rewards=false`;
        }
        if (rewardFilters.search) rewardParams += `&search=${encodeURIComponent(rewardFilters.search)}`;
        endpoint = `/api/rewards?${rewardParams}`;
        break;
      case 'logs':
        let logParams = baseParams;
        if (logFilters.log_type) logParams += `&log_type=${encodeURIComponent(logFilters.log_type)}`;
        if (logFilters.log_type_negative) logParams += `&log_type_negative=true`;
        if (logFilters.company_id) logParams += `&company_id=${logFilters.company_id}`;
        if (logFilters.search) logParams += `&search=${encodeURIComponent(logFilters.search)}`;
        endpoint = `/api/logs?${logParams}`;
        break;
      default:
        endpoint = `/api/users?${baseParams}`;
    }

    const result = await apiCall('get', endpoint);

    if (result.success) {
      const responseData = result.data;
      setData(responseData[selectedTab] || []);
      setTotal(responseData.total || 0);
    } else {
      // Don't show error if it's an authentication error (user will be redirected)
      if (!result.requiresAuth) {
        setError(result.error);
      }
    }

    setLoading(false);
  };

  const loadCompanies = async () => {
    const result = await apiCall('get', '/api/companies');
    if (result.success) {
      setCompanies(result.data.companies || []);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
  };

  const loadRewardProviders = async () => {
    const result = await apiCall('get', '/api/reward-providers');
    if (result.success) {
      setRewardProviders(result.data.reward_providers || []);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
  };

  const loadCompanyRewardProviders = async (companyId) => {
    if (!companyId) return;
    setLoadingRewardProviders(true);
    const result = await apiCall('get', `/api/company-reward-providers?company_id=${companyId}`);
    if (result.success) {
      setCompanyRewardProviders(result.data.company_reward_providers || []);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
    setLoadingRewardProviders(false);
  };

  const addRewardProviderToCompany = async (companyId, rewardProviderId) => {
    const result = await apiCall('post', '/api/company-reward-providers', {
      company_id: companyId,
      reward_provider_id: rewardProviderId
    });
    if (result.success) {
      setSuccess('Reward provider added successfully');
      loadCompanyRewardProviders(companyId);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
  };

  const removeRewardProviderFromCompany = async (relationshipId, companyId) => {
    const result = await apiCall('delete', `/api/company-reward-providers/${relationshipId}`);
    if (result.success) {
      setSuccess('Reward provider removed successfully');
      loadCompanyRewardProviders(companyId);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
  };

  const getAvailableRewardProviders = () => {
    const assignedProviderIds = companyRewardProviders.map(crp => crp.reward_provider_id);
    return rewardProviders.filter(provider => !assignedProviderIds.includes(provider.id));
  };

  const handleEditConfig = (crp) => {
    setEditingConfig(crp);
    setConfigError('');
    // Initialize config JSON from crp.config or config_template
    const initialConfig = crp.config || crp.reward_provider?.config_template || {};
    setConfigJson(JSON.stringify(initialConfig, null, 2));
    setConfigDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    setConfigError('');

    // Validate JSON
    let parsedConfig;
    try {
      parsedConfig = JSON.parse(configJson);
    } catch (e) {
      setConfigError('Invalid JSON format. Please check your syntax.');
      return;
    }

    // Validate against config_template if it exists
    if (editingConfig?.reward_provider?.config_template) {
      const template = editingConfig.reward_provider.config_template;
      // Basic structure validation - check if all required keys from template exist
      const templateKeys = Object.keys(template);
      const configKeys = Object.keys(parsedConfig);
      const missingKeys = templateKeys.filter(key => !configKeys.includes(key));
      if (missingKeys.length > 0) {
        setConfigError(`Missing required keys: ${missingKeys.join(', ')}`);
        return;
      }
    }

    // Save config
    const result = await apiCall('put', `/api/company-reward-providers/${editingConfig.id}`, {
      config: parsedConfig
    });

    if (result.success) {
      setSuccess('Config updated successfully');
      setConfigDialogOpen(false);
      setEditingConfig(null);
      setConfigJson('');
      loadCompanyRewardProviders(editingItem?.id);
    } else if (!result.requiresAuth) {
      setConfigError(result.error || 'Failed to update config');
    }
  };

  const handleCloseConfigDialog = () => {
    setConfigDialogOpen(false);
    setEditingConfig(null);
    setConfigJson('');
    setConfigError('');
  };

  // Extra Modules functions
  const loadExtraModules = async () => {
    const result = await apiCall('get', '/api/extra-modules');
    if (result.success) {
      setExtraModules(result.data.extra_modules || []);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
  };

  const loadCompanyExtraModules = async (companyId) => {
    if (!companyId) return;
    setLoadingExtraModules(true);
    const result = await apiCall('get', `/api/company-extra-modules?company_id=${companyId}`);
    if (result.success) {
      setCompanyExtraModules(result.data.company_extra_modules || []);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
    setLoadingExtraModules(false);
  };

  const addExtraModuleToCompany = async (companyId, extraModuleId) => {
    const result = await apiCall('post', '/api/company-extra-modules', {
      company_id: companyId,
      extra_module_id: extraModuleId
    });
    if (result.success) {
      setSuccess('Extra module added successfully');
      loadCompanyExtraModules(companyId);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
  };

  const removeExtraModuleFromCompany = async (relationshipId, companyId) => {
    const result = await apiCall('delete', `/api/company-extra-modules/${relationshipId}`);
    if (result.success) {
      setSuccess('Extra module removed successfully');
      loadCompanyExtraModules(companyId);
    } else if (!result.requiresAuth) {
      setError(result.error);
    }
  };

  const getAvailableExtraModules = () => {
    const assignedModuleIds = companyExtraModules.map(cem => cem.extra_module_id);
    return extraModules.filter(module => !assignedModuleIds.includes(module.id));
  };

  const handleEditExtraModuleConfig = (cem) => {
    setEditingExtraModuleConfig(cem);
    setExtraModuleConfigError('');
    // Initialize config JSON from cem.config or config_template
    const initialConfig = cem.config || cem.extra_module?.config_template || {};
    setExtraModuleConfigJson(JSON.stringify(initialConfig, null, 2));
    setExtraModuleConfigDialogOpen(true);
  };

  const handleSaveExtraModuleConfig = async () => {
    setExtraModuleConfigError('');

    // Validate JSON
    let parsedConfig;
    try {
      parsedConfig = JSON.parse(extraModuleConfigJson);
    } catch (e) {
      setExtraModuleConfigError('Invalid JSON format. Please check your syntax.');
      return;
    }

    // Validate against config_template if it exists
    if (editingExtraModuleConfig?.extra_module?.config_template) {
      const template = editingExtraModuleConfig.extra_module.config_template;
      // Basic structure validation
      const templateKeys = Object.keys(template);
      const configKeys = Object.keys(parsedConfig);
      const missingKeys = templateKeys.filter(key => !configKeys.includes(key));
      if (missingKeys.length > 0) {
        setExtraModuleConfigError(`Missing required keys: ${missingKeys.join(', ')}`);
        return;
      }
    }

    // Save config
    const result = await apiCall('put', `/api/company-extra-modules/${editingExtraModuleConfig.id}`, {
      config: parsedConfig
    });

    if (result.success) {
      setSuccess('Config updated successfully');
      setExtraModuleConfigDialogOpen(false);
      setEditingExtraModuleConfig(null);
      setExtraModuleConfigJson('');
      loadCompanyExtraModules(editingItem?.id);
    } else if (!result.requiresAuth) {
      setExtraModuleConfigError(result.error || 'Failed to update config');
    }
  };

  const handleCloseExtraModuleConfigDialog = () => {
    setExtraModuleConfigDialogOpen(false);
    setEditingExtraModuleConfig(null);
    setExtraModuleConfigJson('');
    setExtraModuleConfigError('');
  };

  // Tremendous setup functions
  const handleTremendousSetup = async (companyId) => {
    if (!companyId) return;
    setTremendousSetupLoading(true);
    try {
      const result = await apiCall('post', `/api/tremendous/setup/${companyId}`);
      if (result.success) {
        setSuccess('Tremendous setup completed successfully');
        loadCompanyRewardProviders(companyId);
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to setup Tremendous');
      }
    } catch (error) {
      console.error('Error setting up Tremendous:', error);
      setError('Failed to setup Tremendous');
    } finally {
      setTremendousSetupLoading(false);
    }
  };

  const handleOpenTremendousFlow = async (companyId) => {
    if (!companyId) return;
    setTremendousSetupLoading(true);
    try {
      const result = await apiCall('get', `/api/tremendous/session/${companyId}`);
      if (result.success && result.data?.url) {
        // Redirect to Tremendous flow
        window.location.href = result.data.url;
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to get Tremendous session URL');
      }
    } catch (error) {
      console.error('Error opening Tremendous flow:', error);
      setError('Failed to open Tremendous flow');
    } finally {
      setTremendousSetupLoading(false);
    }
  };

  const handleGetTremendousBalance = async (companyId) => {
    if (!companyId) return;
    setTremendousBalanceLoading(true);
    try {
      const result = await apiCall('get', `/api/tremendous/balance/${companyId}`);
      if (result.success) {
        setTremendousBalance(result.data);
      } else if (!result.requiresAuth) {
        setError(result.error || 'Failed to get Tremendous balance');
      }
    } catch (error) {
      console.error('Error getting Tremendous balance:', error);
      setError('Failed to get Tremendous balance');
    } finally {
      setTremendousBalanceLoading(false);
    }
  };

  const getTremendousProvider = () => {
    return companyRewardProviders.find(crp =>
      crp.reward_provider?.name?.toLowerCase().includes('tremendous')
    );
  };

  const getTremendousConnectionStatus = (tremendousProvider) => {
    if (!tremendousProvider) return null;
    const config = tremendousProvider.config || {};
    const hasOAuthToken = !!config.tremendous_oauth_access_token;
    const hasMemberId = !!config.tremendous_connected_organization_member_id;
    const hasOrgId = !!config.tremendous_connected_organization_id;

    if (hasOAuthToken) return 'connected';
    if (hasMemberId && hasOrgId) return 'setup_complete';
    return 'not_setup';
  };

  const handleEdit = (item) => {
    setEditingItem(item);

    // If editing a company with a branding image, construct the full public URL
    const formDataWithUrl = { ...item };
    if (selectedTab === 'companies' && item.branding_image) {
      formDataWithUrl.branding_image_url = `${CDN_URL}/${item.branding_image}`;
      console.log('🔧 Constructed URL for existing image:', formDataWithUrl.branding_image_url); // Debug log
    }

    // If editing a reward with an image, construct the full public URL
    if (selectedTab === 'rewards' && item.reward_image) {
      formDataWithUrl.reward_image_url = `${CDN_URL}/${item.reward_image}`;
      console.log('🔧 Constructed URL for existing reward image:', formDataWithUrl.reward_image_url); // Debug log
    }

    // Initialize rewards-specific fields
    if (selectedTab === 'rewards') {
      if (formDataWithUrl.company_id == null || formDataWithUrl.company_id === undefined) {
        formDataWithUrl.company_id = 0;  // 0 = All companies for form display
      }
      if (!formDataWithUrl.denominations || formDataWithUrl.denominations.length === 0) {
        // Get provider's default currency
        const provider = rewardProviders.find(p => p.id === item.reward_provider_id);
        const defaultCurrency = provider?.default_currency || 'USD';

        // Convert old single denomination to array format
        formDataWithUrl.denominations = [{
          value: formDataWithUrl.denomination || '',
          currency: formDataWithUrl.currency || defaultCurrency,
          is_default: true
        }];
      }
    }

    setFormData(formDataWithUrl);
    setDialogError('');

    // Load reward providers for the company if editing a company
    if (selectedTab === 'companies' && item.id) {
      loadCompanyRewardProviders(item.id);
      loadCompanyExtraModules(item.id);
    }

    setOpenDialog(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    let defaultFormData = {};

    // Set default values for rewards
    if (selectedTab === 'rewards') {
      defaultFormData = {
        company_id: 0,  // 0 = All companies
        currency: 'USD',
        any_value: false,
        denominations: [{ value: '', currency: 'USD', is_default: true }]
      };
    }

    setFormData(defaultFormData);
    setDialogError('');

    // Clear reward providers for new company
    if (selectedTab === 'companies') {
      setCompanyRewardProviders([]);
      setCompanyExtraModules([]);
    }

    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      const result = await apiCall('delete', `/api/${selectedTab}/${id}`);
      if (result.success) {
        setSuccess('Item deleted successfully');
        loadData();
      } else if (!result.requiresAuth) {
        setError(result.error);
      }
    }
  };

  const handleSelectAllUsers = (event) => {
    if (event.target.checked) {
      const allUserIds = data.map(user => user.id);
      setSelectedUserIds(allUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleUserDelete = (userId) => {
    setDeletingUserId(userId);
    setOpenDeleteUserDialog(true);
    setDeleteUserConfirmed(false);
    setDialogError('');
  };

  const handleUserDeleteConfirm = async () => {
    if (!deleteUserConfirmed) {
      setDialogError('Please confirm the deletion by checking the confirmation box');
      return;
    }

    setLoading(true);
    const result = await apiCall('delete', `/api/users/${deletingUserId}`);
    setLoading(false);

    if (result.success) {
      setSuccess('User deleted successfully');
      setOpenDeleteUserDialog(false);
      setDeletingUserId(null);
      setDeleteUserConfirmed(false);
      setDialogError('');
      loadData();
    } else if (!result.requiresAuth) {
      setDialogError(result.error);
    }
  };

  const handleBulkDelete = () => {
    setOpenBulkDeleteDialog(true);
    setBulkDeleteConfirmed(false);
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkDeleteConfirmed) {
      setDialogError('Please confirm the deletion by checking the confirmation box');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const userId of selectedUserIds) {
      const result = await apiCall('delete', `/api/users/${userId}`);
      if (result.success) {
        successCount++;
      } else {
        errorCount++;
      }
    }

    setLoading(false);
    setOpenBulkDeleteDialog(false);
    setBulkDeleteConfirmed(false);
    setSelectedUserIds([]);

    if (successCount > 0) {
      setSuccess(`Successfully deleted ${successCount} user(s)${errorCount > 0 ? `. Failed to delete ${errorCount} user(s)` : ''}`);
    }
    if (errorCount > 0 && successCount === 0) {
      setError(`Failed to delete ${errorCount} user(s)`);
    }

    loadData();
  };

  const handleSelectAllDealers = (event) => {
    if (event.target.checked) {
      const allDealerIds = data.map(dealer => dealer.id);
      setSelectedDealerIds(allDealerIds);
    } else {
      setSelectedDealerIds([]);
    }
  };

  const handleSelectDealer = (dealerId) => {
    setSelectedDealerIds(prev => {
      if (prev.includes(dealerId)) {
        return prev.filter(id => id !== dealerId);
      } else {
        return [...prev, dealerId];
      }
    });
  };

  const handleDealerDelete = (dealerId) => {
    setDeletingDealerId(dealerId);
    setOpenDeleteDealerDialog(true);
    setDeleteDealerConfirmed(false);
    setDialogError('');
  };

  const handleDealerDeleteConfirm = async () => {
    if (!deleteDealerConfirmed) {
      setDialogError('Please confirm the deletion by checking the confirmation box');
      return;
    }

    setLoading(true);
    const result = await apiCall('delete', `/api/dealers/${deletingDealerId}`);
    setLoading(false);

    if (result.success) {
      setSuccess('Dealer deleted successfully');
      setOpenDeleteDealerDialog(false);
      setDeletingDealerId(null);
      setDeleteDealerConfirmed(false);
      setDialogError('');
      loadData();
    } else if (!result.requiresAuth) {
      setDialogError(result.error);
    }
  };

  const handleBulkDeleteDealers = () => {
    setOpenBulkDeleteDialog(true);
    setBulkDeleteConfirmed(false);
    setDialogError('');
  };

  const handleBulkDeleteDealersConfirm = async () => {
    if (!bulkDeleteConfirmed) {
      setDialogError('Please confirm the deletion by checking the confirmation box');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const dealerId of selectedDealerIds) {
      const result = await apiCall('delete', `/api/dealers/${dealerId}`);
      if (result.success) {
        successCount++;
      } else {
        errorCount++;
      }
    }

    setLoading(false);
    setOpenBulkDeleteDialog(false);
    setBulkDeleteConfirmed(false);
    setSelectedDealerIds([]);

    if (successCount > 0) {
      setSuccess(`Successfully deleted ${successCount} dealer(s)${errorCount > 0 ? `. Failed to delete ${errorCount} dealer(s)` : ''}`);
    }
    if (errorCount > 0 && successCount === 0) {
      setError(`Failed to delete ${errorCount} dealer(s)`);
    }

    loadData();
  };

  const handleSave = async () => {
    setDialogError('');
    setSuccess('');

    const method = editingItem ? 'put' : 'post';
    const endpoint = editingItem ? `/api/${selectedTab}/${editingItem.id}` : `/api/${selectedTab}`;

    // Process formData before sending
    let processedFormData = { ...formData };

    // Special handling for rewards - company_id: 0 = All companies
    if (selectedTab === 'rewards') {
      const cid = processedFormData.company_id;
      processedFormData.company_id = (cid === '' || cid === null || cid === undefined) ? 0 : cid;
    }

    // Special handling for dealers - ensure campaigns is properly formatted
    if (selectedTab === 'dealers' && processedFormData.campaigns) {
      if (typeof processedFormData.campaigns === 'string') {
        // Convert string to array if user finished editing campaigns as string
        processedFormData.campaigns = processedFormData.campaigns
          .split(',')
          .map(s => s.trim())
          .filter(s => s);
      }
    }

    if (selectedTab === 'users') {
      const cid = processedFormData.company_id;
      if (cid !== '' && cid != null) {
        processedFormData.company_id = typeof cid === 'string' ? parseInt(cid, 10) : cid;
      }
      if (processedFormData.role === 'Dealer') {
        const did = processedFormData.dealer_id;
        if (did === '' || did === undefined) {
          processedFormData.dealer_id = null;
        } else {
          processedFormData.dealer_id = typeof did === 'string' ? parseInt(did, 10) : did;
        }
      } else {
        delete processedFormData.dealer_id;
        delete processedFormData.activation_url;
      }
    }

    const result = await apiCall(method, endpoint, processedFormData);

    if (result.success) {
      setSuccess(editingItem ? 'Item updated successfully' : 'Item created successfully');
      setOpenDialog(false);
      setEditingItem(null);
      setFormData({});
      setDialogError('');
      loadData();

      // Reload companies list if we just created/updated a company
      // This ensures the company appears in dropdowns (like "Add user")
      if (selectedTab === 'companies') {
        loadCompanies();
      }
    } else {
      // Don't show error if it's an authentication error (user will be redirected)
      if (!result.requiresAuth) {
        setDialogError(result.error);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleImpersonate = async (userId) => {
    try {
      setLoading(true);
      const result = await startImpersonation(userId);

      if (result.success) {
        // Show success message
        setSuccess(`Now impersonating ${result.user.name} (${result.user.email})`);

        // Redirect based on the impersonated user's role
        if (result.user.role === 'SuperAdmin' || result.user.role === 'Sales') {
          navigate('/admin');
        } else if (result.user.role === 'Admin' || result.user.role === 'Manager') {
          navigate('/manufacturer');
        } else if (result.user.role === 'Dealer') {
          navigate('/dealer');
        } else {
          navigate('/dealer');
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to impersonate user');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0); // Reset to first page when filtering
  };

  const handleCompanyFilterChange = (filterType, value) => {
    setCompanyFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      company_id: '',
      role: '',
      search: ''
    });
    setPage(0);
  };

  const clearCompanyFilters = () => {
    setCompanyFilters({
      country: '',
      tier: '',
      search: ''
    });
    setPage(0);
  };

  const handleRewardFilterChange = (filterType, value) => {
    setRewardFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0); // Reset to first page when filtering
  };

  const clearRewardFilters = () => {
    setRewardFilters({
      provider_id: '',
      company_id: '',
      search: ''
    });
    setPage(0);
  };

  const handleLogFilterChange = (filterType, value) => {
    setLogFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0); // Reset to first page when filtering
  };

  const clearLogFilters = () => {
    setLogFilters({
      log_type: '',
      log_type_negative: false,
      company_id: '',
      search: ''
    });
    setPage(0);
  };

  const renderFormFields = () => {
    switch (selectedTab) {
      case 'users':
        return (
          <>
            <TextField
              margin="dense"
              label="Name"
              fullWidth
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Last Name"
              fullWidth
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            {!editingItem && (
              <TextField
                margin="dense"
                label="Password"
                type="password"
                fullWidth
                value={formData.password || ''}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            )}
            <TextField
              margin="dense"
              label="Role"
              select
              fullWidth
              value={formData.role || ''}
              onChange={(e) => {
                const newRole = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  role: newRole,
                  ...(newRole !== 'Dealer' ? { dealer_id: undefined } : {}),
                }));
              }}
              required
            >
              <MenuItem value="SuperAdmin">SuperAdmin</MenuItem>
              <MenuItem value="Sales">Sales</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Dealer">Dealer</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              label="Company"
              select
              fullWidth
              value={formData.company_id ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  company_id: v === '' ? '' : (typeof v === 'string' ? parseInt(v, 10) : v),
                  ...(String(v) !== String(prev.company_id ?? '') ? { dealer_id: undefined } : {}),
                }));
              }}
              required
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            {formData.role === 'Dealer' && (
              <TextField
                margin="dense"
                label="Dealer"
                select
                fullWidth
                value={formData.dealer_id != null && formData.dealer_id !== '' ? formData.dealer_id : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    dealer_id: v === '' ? undefined : parseInt(v, 10),
                  }));
                }}
                disabled={!formData.company_id || loadingUserFormDealers}
                helperText={
                  loadingUserFormDealers
                    ? 'Loading dealers…'
                    : !formData.company_id
                      ? 'Select a company first'
                      : userFormDealers.length === 0
                        ? 'No dealers for this company'
                        : ''
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {userFormDealers.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {formData.role === 'Dealer' && (
              <TextField
                margin="dense"
                label="Activation URL"
                fullWidth
                value={formData.activation_url ?? ''}
                onChange={(e) => setFormData({ ...formData, activation_url: e.target.value })}
                helperText="Used for campaign-start emails when this dealer has never logged in. Leave blank to clear."
              />
            )}
          </>
        );

      case 'dealers':
        return (
          <>
            <TextField
              margin="dense"
              label="Name"
              fullWidth
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Company"
              select
              fullWidth
              value={formData.company_id || ''}
              onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
              required
            >
              <MenuItem value="">Select a company</MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              label="Salesforce ID"
              fullWidth
              value={formData.sf_id || ''}
              onChange={(e) => setFormData({ ...formData, sf_id: e.target.value })}
              placeholder="Enter Salesforce ID"
            />
            <TextField
              margin="dense"
              label="Segmentation"
              fullWidth
              value={formData.segmentation || ''}
              onChange={(e) => setFormData({ ...formData, segmentation: e.target.value })}
              placeholder="Enter segmentation"
            />
            <TextField
              margin="dense"
              label="City"
              fullWidth
              value={formData.city || ''}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="Enter city"
            />
            <TextField
              margin="dense"
              label="ASM Owner Name"
              fullWidth
              value={formData.asm_owner_name || ''}
              onChange={(e) => setFormData({ ...formData, asm_owner_name: e.target.value })}
              placeholder="Enter Account Sales Manager owner name"
            />
            <TextField
              margin="dense"
              label="Campaigns"
              fullWidth
              value={Array.isArray(formData.campaigns) ? formData.campaigns.join(', ') : (formData.campaigns || '')}
              onChange={(e) => {
                // Store the raw string value without processing
                setFormData({ ...formData, campaigns: e.target.value });
              }}
              onBlur={(e) => {
                // Process campaigns when field loses focus
                const campaignsString = e.target.value;
                if (campaignsString) {
                  const campaignsArray = campaignsString.split(',').map(s => s.trim()).filter(s => s);
                  setFormData({ ...formData, campaigns: campaignsArray });
                } else {
                  setFormData({ ...formData, campaigns: [] });
                }
              }}
              placeholder="Enter campaigns separated by commas (e.g., 2024S1, 2025S1, 2025Y1)"
              helperText="Separate multiple campaigns with commas"
              multiline
              rows={2}
            />
          </>
        );

      case 'companies':
        return (
          <>
            <TextField
              margin="dense"
              label="Name"
              fullWidth
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Country"
              fullWidth
              value={formData.country || ''}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Tier"
              fullWidth
              value={formData.tier || ''}
              onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
            />
            <TextField
              margin="dense"
              label="API Account"
              fullWidth
              value={formData.api_account || ''}
              onChange={(e) => setFormData({ ...formData, api_account: e.target.value })}
              placeholder="Enter API account identifier"
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <TextField
                margin="dense"
                label="Branding Color"
                type="color"
                value={formData.branding_color || '#6C3BF8'}
                onChange={(e) => setFormData({ ...formData, branding_color: e.target.value })}
                helperText="Primary brand color"
                sx={{ flex: 1 }}
              />
              <TextField
                margin="dense"
                label="Branding Button Color"
                type="color"
                value={formData.branding_button_color || '#5B32D6'}
                onChange={(e) => setFormData({ ...formData, branding_button_color: e.target.value })}
                helperText="Button/accent color"
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="branding-image-upload"
                type="file"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    try {
                      console.log('Starting file upload...', file.name); // Debug log

                      // Upload file to backend
                      const uploadFormData = new FormData();
                      uploadFormData.append('file', file);

                      console.log('Sending request to:', `${API_URL}/api/upload/branding-image`); // Debug log

                      const response = await fetch(`${API_URL}/api/upload/branding-image`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                        },
                        body: uploadFormData,
                      });

                      console.log('Response status:', response.status); // Debug log
                      console.log('Response ok:', response.ok); // Debug log

                      if (response.ok) {
                        const result = await response.json();
                        console.log('✅ Upload result:', result); // Debug log
                        console.log('✅ Result has public_url:', !!result.public_url); // Debug log
                        console.log('✅ Public URL value:', result.public_url); // Debug log

                        setFormData(prev => {
                          const newData = {
                            ...prev,
                            branding_image: result.filename,
                            branding_image_url: result.public_url
                          };
                          console.log('✅ Setting formData:', newData); // Debug log
                          console.log('✅ branding_image_url in newData:', newData.branding_image_url); // Debug log
                          return newData;
                        });
                      } else {
                        console.log('❌ Response not ok, status:', response.status); // Debug log
                        const errorData = await response.json();
                        console.log('❌ Error data:', errorData); // Debug log
                        setDialogError(errorData.detail || 'Failed to upload image');
                      }
                    } catch (error) {
                      console.log('❌ Upload error:', error); // Debug log
                      setDialogError('Error uploading image: ' + error.message);
                    }
                  }
                }}
              />
              <label htmlFor="branding-image-upload">
                <Button variant="outlined" component="span" sx={{ mr: 2 }}>
                  Upload Branding Image
                </Button>
              </label>
              {formData.branding_image && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Selected: {formData.branding_image}
                  </Typography>
                  {console.log('Preview check - branding_image_url:', formData.branding_image_url)} {/* Debug log */}
                  {formData.branding_image_url ? (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'inline-block' }}>
                      <img
                        src={formData.branding_image_url}
                        alt="Branding preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '100px',
                          objectFit: 'contain'
                        }}
                        onLoad={() => console.log('Image loaded successfully')}
                        onError={(e) => console.log('Image load error:', e)}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      No preview URL available
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <TextField
              margin="dense"
              label="Dealer dashboard welcome"
              fullWidth
              value={formData.dashboard_welcome || ''}
              onChange={(e) => setFormData({ ...formData, dashboard_welcome: e.target.value })}
              placeholder="Welcome to the contest platform!"
              helperText="Hero heading on the dealer dashboard. Leave empty to use the default translation."
              multiline
              minRows={2}
              sx={{ mt: 2 }}
            />

            {/* Reward Providers Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Reward Providers
              </Typography>

              {/* Add Reward Provider */}
              {editingItem && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Add Reward Provider</InputLabel>
                    <Select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          addRewardProviderToCompany(editingItem.id, e.target.value);
                        }
                      }}
                      disabled={loadingRewardProviders}
                    >
                      {getAvailableRewardProviders().map((provider) => (
                        <MenuItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select a reward provider to add to this company
                    </FormHelperText>
                  </FormControl>
                </Box>
              )}

              {/* Current Reward Providers */}
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                {loadingRewardProviders ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : companyRewardProviders.length > 0 ? (
                  <List dense>
                    {companyRewardProviders.map((crp) => (
                      <ListItem key={crp.id}>
                        <ListItemText
                          primary={crp.reward_provider?.display_name || crp.reward_provider?.name || 'Unknown Provider'}
                          secondary={`Added: ${formatDateShort(crp.created)}`}
                        />
                        <ListItemSecondaryAction>
                          {crp.reward_provider?.custom_config && (
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleEditConfig(crp)}
                              disabled={loadingRewardProviders}
                              sx={{ mr: 1 }}
                              title="Edit Config"
                            >
                              <SettingsIcon />
                            </IconButton>
                          )}
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => removeRewardProviderFromCompany(crp.id, editingItem?.id)}
                            disabled={loadingRewardProviders}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No reward providers assigned to this company
                  </Typography>
                )}
              </Box>

              {!editingItem && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Save the company first to assign reward providers
                </Alert>
              )}

              {/* Extra Modules Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Extra Modules
                </Typography>

                {/* Add Extra Module */}
                {editingItem && (
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Add Extra Module</InputLabel>
                      <Select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            addExtraModuleToCompany(editingItem.id, e.target.value);
                          }
                        }}
                        disabled={loadingExtraModules}
                      >
                        {getAvailableExtraModules().map((module) => (
                          <MenuItem key={module.id} value={module.id}>
                            {module.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select an extra module to add to this company
                      </FormHelperText>
                    </FormControl>
                  </Box>
                )}

                {/* Current Extra Modules */}
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  {loadingExtraModules ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : companyExtraModules.length > 0 ? (
                    <List dense>
                      {companyExtraModules.map((cem) => (
                        <ListItem key={cem.id}>
                          <ListItemText
                            primary={cem.extra_module?.name || 'Unknown Module'}
                            secondary={`Added: ${formatDateShort(cem.created)}`}
                          />
                          <ListItemSecondaryAction>
                            {cem.extra_module?.custom_config && (
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleEditExtraModuleConfig(cem)}
                                disabled={loadingExtraModules}
                                sx={{ mr: 1 }}
                                title="Edit Config"
                              >
                                <SettingsIcon />
                              </IconButton>
                            )}
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => removeExtraModuleFromCompany(cem.id, editingItem?.id)}
                              disabled={loadingExtraModules}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No extra modules assigned to this company
                    </Typography>
                  )}
                </Box>

                {!editingItem && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Save the company first to assign extra modules
                  </Alert>
                )}
              </Box>

              {/* Tremendous Integration Section */}
              {editingItem && selectedTab === 'companies' && (() => {
                const tremendousProvider = getTremendousProvider();
                const connectionStatus = getTremendousConnectionStatus(tremendousProvider);

                // Check if Tremendous reward provider exists in the system
                const tremendousRewardProvider = rewardProviders.find(rp =>
                  rp.name?.toLowerCase().includes('tremendous')
                );

                if (!tremendousRewardProvider) {
                  return (
                    <Box sx={{ mt: 4, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Tremendous reward provider not found in the system. Please add it first.
                      </Typography>
                    </Box>
                  );
                }

                if (!tremendousProvider) {
                  return (
                    <Box sx={{ mt: 4, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Tremendous Integration
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add Tremendous as a reward provider above to enable integration.
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <Box sx={{ mt: 4, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Tremendous Integration
                    </Typography>

                    {connectionStatus === 'not_setup' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Tremendous is added but not yet set up. Click below to initialize the connection.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleTremendousSetup(editingItem.id)}
                          disabled={tremendousSetupLoading}
                          startIcon={tremendousSetupLoading ? <CircularProgress size={20} /> : <SettingsIcon />}
                        >
                          Set up on Tremendous
                        </Button>
                      </Box>
                    )}

                    {connectionStatus === 'setup_complete' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Tremendous is set up. Complete the OAuth flow to connect your account.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleOpenTremendousFlow(editingItem.id)}
                          disabled={tremendousSetupLoading}
                          startIcon={tremendousSetupLoading ? <CircularProgress size={20} /> : <SettingsIcon />}
                        >
                          Open Tremendous flow
                        </Button>
                      </Box>
                    )}

                    {connectionStatus === 'connected' && (
                      <Box>
                        <Chip
                          label="Connected"
                          color="success"
                          sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleGetTremendousBalance(editingItem.id)}
                            disabled={tremendousBalanceLoading}
                            startIcon={tremendousBalanceLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                          >
                            Available balance
                          </Button>
                        </Box>

                        {tremendousBalance && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Available Balance:
                            </Typography>
                            {tremendousBalance.funding_sources && tremendousBalance.funding_sources.length > 0 ? (
                              tremendousBalance.funding_sources
                                .filter(fs => fs.method === 'balance' && fs.meta?.available_cents)
                                .map((fs, idx) => (
                                  <Typography key={idx} variant="body1" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(fs.meta.available_cents / 100, 'USD')}
                                  </Typography>
                                ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No balance information available
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          </>
        );

      case 'rewards':
        return (
          <>
            <TextField
              margin="dense"
              label="Reward Provider"
              select
              fullWidth
              value={formData.reward_provider_id || ''}
              onChange={(e) => setFormData({ ...formData, reward_provider_id: e.target.value })}
              required
            >
              {rewardProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.display_name || provider.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              label="Company"
              select
              fullWidth
              value={formData.company_id === 0 || formData.company_id === null || formData.company_id === undefined ? '' : formData.company_id}
              onChange={(e) => setFormData({ ...formData, company_id: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })}
              helperText="All = available to all companies"
            >
              <MenuItem value="">
                All
              </MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              label="Name"
              fullWidth
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Code"
              fullWidth
              value={formData.code || ''}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Product Code"
              fullWidth
              value={formData.product_code || ''}
              onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
            />

            {/* Denominations Array Manager */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Denominations *
                </Typography>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={formData.any_value || false}
                      onChange={(e) => setFormData({ ...formData, any_value: e.target.checked })}
                      size="small"
                    />
                    <Typography variant="caption">Any value</Typography>
                  </Box>
                </FormControl>
              </Box>
              {(formData.denominations || []).map((denom, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    label="Value"
                    type="number"
                    size="small"
                    value={denom.value || ''}
                    onChange={(e) => {
                      const newDenominations = [...(formData.denominations || [])];
                      newDenominations[index] = {
                        ...newDenominations[index],
                        value: parseFloat(e.target.value) || 0
                      };
                      setFormData({ ...formData, denominations: newDenominations });
                    }}
                    required
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Currency"
                    select
                    size="small"
                    value={denom.currency || 'USD'}
                    onChange={(e) => {
                      const newDenominations = [...(formData.denominations || [])];
                      newDenominations[index] = {
                        ...newDenominations[index],
                        currency: e.target.value
                      };
                      setFormData({ ...formData, denominations: newDenominations });
                    }}
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="PLN">PLN</MenuItem>
                    <MenuItem value="HUF">HUF</MenuItem>
                    <MenuItem value="RON">RON</MenuItem>
                  </TextField>
                  <TextField
                    label="Code"
                    size="small"
                    value={denom.code || ''}
                    onChange={(e) => {
                      const newDenominations = [...(formData.denominations || [])];
                      newDenominations[index] = {
                        ...newDenominations[index],
                        code: e.target.value
                      };
                      setFormData({ ...formData, denominations: newDenominations });
                    }}
                    sx={{ flex: 1 }}
                    placeholder="Optional"
                  />
                  <FormControl component="fieldset" sx={{ minWidth: 80 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={denom.is_default || false}
                        onChange={(e) => {
                          const newDenominations = [...(formData.denominations || [])];
                          // Uncheck all others and check this one
                          newDenominations.forEach((d, i) => {
                            newDenominations[i] = {
                              ...d,
                              is_default: i === index
                            };
                          });
                          setFormData({ ...formData, denominations: newDenominations });
                        }}
                        size="small"
                      />
                      <Typography variant="caption">Default</Typography>
                    </Box>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newDenominations = (formData.denominations || []).filter((_, i) => i !== index);
                      // If we removed the default, make the first one default
                      if (newDenominations.length > 0 && !newDenominations.some(d => d.is_default)) {
                        newDenominations[0].is_default = true;
                      }
                      setFormData({ ...formData, denominations: newDenominations });
                    }}
                    disabled={(formData.denominations || []).length <= 1}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => {
                  // Get provider's default currency
                  const provider = rewardProviders.find(p => p.id === formData.reward_provider_id);
                  const defaultCurrency = provider?.default_currency || 'USD';

                  const newDenominations = [
                    ...(formData.denominations || []),
                    { value: '', currency: defaultCurrency, is_default: false }
                  ];
                  setFormData({ ...formData, denominations: newDenominations });
                }}
                sx={{ mt: 1 }}
              >
                Add Denomination
              </Button>
            </Box>

            <TextField
              margin="dense"
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            {/* Reward Image Upload */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Reward Image
              </Typography>

              {/* Upload Button */}
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="reward-image-upload"
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        console.log('Starting reward image upload...', file.name);

                        // Upload file to backend
                        const uploadFormData = new FormData();
                        uploadFormData.append('file', file);

                        console.log('Sending request to:', `${API_URL}/api/upload/reward-image`);

                        const response = await fetch(`${API_URL}/api/upload/reward-image`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                          },
                          body: uploadFormData,
                        });

                        console.log('Response status:', response.status);

                        if (response.ok) {
                          const result = await response.json();
                          console.log('✅ Reward image upload result:', result);

                          setFormData(prev => {
                            const newData = {
                              ...prev,
                              reward_image: result.filename,
                              reward_image_url: result.public_url
                            };
                            console.log('✅ Setting reward formData:', newData);
                            return newData;
                          });
                        } else {
                          console.log('❌ Response not ok, status:', response.status);
                          const errorData = await response.json();
                          console.log('❌ Error data:', errorData);
                          setDialogError(errorData.detail || 'Failed to upload reward image');
                        }
                      } catch (error) {
                        console.log('❌ Reward image upload error:', error);
                        setDialogError('Error uploading reward image: ' + error.message);
                      }
                    }
                  }}
                />
                <label htmlFor="reward-image-upload">
                  <Button variant="outlined" component="span" sx={{ mr: 2 }}>
                    Upload New Image
                  </Button>
                </label>
              </Box>

              {/* Manual Image Path Input */}
              <TextField
                margin="dense"
                label="Or enter image path manually"
                fullWidth
                value={formData.reward_image || ''}
                onChange={(e) => {
                  const imagePath = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    reward_image: imagePath,
                    reward_image_url: imagePath ? `${CDN_URL}/${imagePath}` : null
                  }));
                }}
                placeholder="e.g., rewards/35a504ba-ccb9-4560-a028-5de745dce514.png"
                helperText="Enter the full path to reuse an existing image"
              />

              {/* Image Preview */}
              {formData.reward_image && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Selected: {formData.reward_image}
                  </Typography>
                  {formData.reward_image_url ? (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'inline-block' }}>
                      <img
                        src={formData.reward_image_url}
                        alt="Reward preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '100px',
                          objectFit: 'contain'
                        }}
                        onLoad={() => console.log('Reward image loaded successfully')}
                        onError={(e) => {
                          console.log('Reward image load error:', e);
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      No preview URL available
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </>
        );

      default:
        return null;
    }
  };

  const renderTableHeaders = () => {
    switch (selectedTab) {
      case 'users':
        return (
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < data.length}
                checked={data.length > 0 && selectedUserIds.length === data.length}
                onChange={handleSelectAllUsers}
              />
            </TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Dealer</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        );

      case 'companies':
        return (
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Country</TableCell>
            <TableCell>Tier</TableCell>
            <TableCell>API Account</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        );

      case 'dealers':
        return (
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selectedDealerIds.length > 0 && selectedDealerIds.length < data.length}
                checked={data.length > 0 && selectedDealerIds.length === data.length}
                onChange={handleSelectAllDealers}
              />
            </TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Salesforce ID</TableCell>
            <TableCell>Segmentation</TableCell>
            <TableCell>City</TableCell>
            <TableCell>ASM Owner</TableCell>
            <TableCell>Campaigns</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        );

      case 'rewards':
        return (
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Denomination</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>Image</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        );

      case 'logs':
        return (
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        );

      default:
        return null;
    }
  };

  const renderTableRow = (item) => {
    switch (selectedTab) {
      case 'users':
        const isSelected = selectedUserIds.includes(item.id);
        return (
          <TableRow key={item.id} selected={isSelected}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={isSelected}
                onChange={() => handleSelectUser(item.id)}
              />
            </TableCell>
            <TableCell>{item.id}</TableCell>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.last_name || 'N/A'}</TableCell>
            <TableCell>{item.email}</TableCell>
            <TableCell>
              <Chip
                label={item.role}
                color={
                  item.role === 'SuperAdmin' ? 'error' :
                    item.role === 'Sales' ? 'warning' :
                      item.role === 'Admin' ? 'success' :
                        'primary'
                }
                size="small"
              />
            </TableCell>
            <TableCell>{item.company?.name || 'N/A'}</TableCell>
            <TableCell>{item.dealer?.name || 'N/A'}</TableCell>
            <TableCell>{formatDateShort(item.created)}</TableCell>
            <TableCell>
              <IconButton onClick={() => handleEdit(item)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleUserDelete(item.id)} size="small">
                <DeleteIcon />
              </IconButton>
              {isSuperAdmin() && item.id !== user.id && (
                <IconButton
                  onClick={() => handleImpersonate(item.id)}
                  size="small"
                  title={`Impersonate ${item.name}`}
                >
                  <PersonAddIcon />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        );

      case 'companies':
        return (
          <TableRow key={item.id}>
            <TableCell>{item.id}</TableCell>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.country}</TableCell>
            <TableCell>{item.tier || 'N/A'}</TableCell>
            <TableCell>{item.api_account || 'N/A'}</TableCell>
            <TableCell>{formatDateShort(item.created)}</TableCell>
            <TableCell>
              <IconButton onClick={() => handleEdit(item)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(item.id)} size="small">
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );

      case 'dealers':
        const isDealerSelected = selectedDealerIds.includes(item.id);
        return (
          <TableRow key={item.id} selected={isDealerSelected}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={isDealerSelected}
                onChange={() => handleSelectDealer(item.id)}
              />
            </TableCell>
            <TableCell>{item.id}</TableCell>
            <TableCell>{item.name}</TableCell>
            <TableCell>{companies.find(c => c.id === item.company_id)?.name || 'N/A'}</TableCell>
            <TableCell>{item.sf_id || 'N/A'}</TableCell>
            <TableCell>{item.segmentation || 'N/A'}</TableCell>
            <TableCell>{item.city || 'N/A'}</TableCell>
            <TableCell>{item.asm_owner_name || 'N/A'}</TableCell>
            <TableCell>
              {item.campaigns && item.campaigns.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {item.campaigns.map((campaign, index) => (
                    <Chip
                      key={index}
                      label={campaign}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              ) : (
                'N/A'
              )}
            </TableCell>
            <TableCell>{formatDateShort(item.created)}</TableCell>
            <TableCell>
              <IconButton onClick={() => handleEdit(item)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDealerDelete(item.id)} size="small">
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );

      case 'rewards':
        const defaultDenom = item.denominations?.find(d => d.is_default) || item.denominations?.[0];
        const hasMultipleDenoms = item.denominations && item.denominations.length > 1;
        return (
          <TableRow key={item.id}>
            <TableCell>{item.id}</TableCell>
            <TableCell>{item.reward_provider?.display_name || item.reward_provider?.name || 'N/A'}</TableCell>
            <TableCell>
              {item.company_id == null || item.company_id === 0
                ? 'All companies'
                : (companies.find((c) => c.id === item.company_id)?.name || `ID ${item.company_id}`)}
            </TableCell>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.code}</TableCell>
            <TableCell>
              {hasMultipleDenoms ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Multiple
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.denominations.map(d => `${d.value} ${d.currency}`).join(', ')}
                  </Typography>
                </Box>
              ) : (
                defaultDenom?.value || item.denomination
                  ? formatCurrency(defaultDenom?.value || item.denomination, defaultDenom?.currency || item.currency || 'USD')
                  : 'N/A'
              )}
            </TableCell>
            <TableCell>
              {hasMultipleDenoms ? (
                <Typography variant="body2" color="text.secondary">
                  Mixed
                </Typography>
              ) : (
                <Chip
                  label={defaultDenom?.currency || item.currency || 'USD'}
                  color="primary"
                  size="small"
                />
              )}
            </TableCell>
            <TableCell>
              {item.reward_image ? (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5, display: 'inline-block' }}>
                  <img
                    src={`${CDN_URL}/${item.reward_image}`}
                    alt={item.name}
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No image
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <IconButton onClick={() => handleEdit(item)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(item.id)} size="small">
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );

      case 'logs':
        return (
          <TableRow key={item.id}>
            <TableCell>{item.id}</TableCell>
            <TableCell>
              <Chip
                label={item.type}
                color={
                  item.type === 'ERROR' ? 'error' :
                    item.type === 'LOGIN' ? 'success' :
                      item.type === 'LOGOUT' ? 'warning' :
                        item.type === 'SYSTEM' ? 'secondary' :
                          'default'
                }
                size="small"
              />
            </TableCell>
            <TableCell>{item.user?.name || 'N/A'}</TableCell>
            <TableCell>{item.user_email || 'N/A'}</TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.description || 'N/A'}
              </Typography>
            </TableCell>
            <TableCell>{formatDateWithTimezone(item.created)}</TableCell>
          </TableRow>
        );

      default:
        return null;
    }
  };

  const allMenuItems = [
    { key: 'companies', label: 'Manufacturers', icon: BusinessIcon },
    { key: 'dealers', label: 'Dealers', icon: StoreIcon },
    { key: 'users', label: 'Users', icon: PeopleIcon },
    { key: 'rewards', label: 'Rewards', icon: CardIcon },
    { key: 'sales', label: 'Sales', icon: SalesIcon },
    { key: 'statistics', label: 'Statistics', icon: BarChartIcon },
    { key: 'logs', label: 'Logs', icon: AssignmentIcon },
    { key: 'settings', label: 'Settings', icon: SettingsIcon },
  ];
  const menuItems = user?.role === 'Sales'
    ? allMenuItems.filter((item) => item.key === 'sales' || item.key === 'settings')
    : allMenuItems;

  // Show loading while authentication is being checked
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ width: `calc(100% - ${currentDrawerWidth}px)`, ml: `${currentDrawerWidth}px` }}>
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            SMOD Admin Panel
          </Typography>
          {isImpersonating && (
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                Impersonating: {user?.name}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={async () => {
                  const result = await stopImpersonation();
                  if (result.success) {
                    setSuccess('Stopped impersonating, returned to original user');
                    window.location.reload(); // Refresh to update the UI
                  } else {
                    setError(result.error);
                  }
                }}
              >
                Stop
              </Button>
            </Box>
          )}
          <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
            Welcome, {isImpersonating ? originalUser?.name : user?.name}
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={!isNarrow ? <LogoutIcon /> : null}>
            {isNarrow ? <LogoutIcon /> : 'Logout'}
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: currentDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={selectedTab === item.key}
                  onClick={() => setSelectedTab(item.key)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isNarrow ? 'center' : 'initial',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isNarrow ? 'auto' : 3,
                      justifyContent: 'center',
                    }}
                  >
                    <item.icon />
                  </ListItemIcon>
                  {!isNarrow && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {isImpersonating && !isNarrow && (
            <Box sx={{ p: 2, mt: 2, bgcolor: 'warning.light', borderRadius: 1, mx: 1 }}>
              <Typography variant="caption" sx={{ color: 'warning.contrastText', fontWeight: 'bold' }}>
                Impersonating
              </Typography>
              <Typography variant="body2" sx={{ color: 'warning.contrastText' }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'warning.contrastText' }}>
                Original: {originalUser?.name}
              </Typography>
            </Box>
          )}
          {!isMobile && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: isNarrow ? 'center' : 'flex-end', p: 1 }}>
                <IconButton
                  onClick={toggleSidebar}
                  size="small"
                  aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
                  sx={{ color: 'text.secondary' }}
                >
                  {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: 'background.default',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <Toolbar />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        )}

        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning('')}>
            {warning}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            {selectedTab === 'companies' ? 'Manufacturers' : selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {selectedTab === 'users' && selectedUserIds.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedUserIds.length})
              </Button>
            )}
            {selectedTab === 'dealers' && selectedDealerIds.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDeleteDealers}
              >
                Delete Selected ({selectedDealerIds.length})
              </Button>
            )}
            {selectedTab !== 'logs' && selectedTab !== 'settings' && selectedTab !== 'sales' && selectedTab !== 'statistics' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
                Add {selectedTab === 'companies' ? 'manufacturer' : selectedTab === 'dealers' ? 'dealer' : selectedTab.slice(0, -1)}
              </Button>
            )}
          </Box>
        </Box>

        {selectedTab === 'users' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Search"
                placeholder="Search by name or email..."
                variant="outlined"
                size="small"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Manufacturer"
                select
                variant="outlined"
                size="small"
                value={filters.company_id}
                onChange={(e) => handleFilterChange('company_id', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Manufacturers</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Role"
                select
                variant="outlined"
                size="small"
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="SuperAdmin">SuperAdmin</MenuItem>
                <MenuItem value="Sales">Sales</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Dealer">Dealer</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={!filters.search && !filters.company_id && !filters.role}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}

        {selectedTab === 'companies' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Search"
                placeholder="Search by company name..."
                variant="outlined"
                size="small"
                value={companyFilters.search}
                onChange={(e) => handleCompanyFilterChange('search', e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Country"
                select
                variant="outlined"
                size="small"
                value={companyFilters.country}
                onChange={(e) => handleCompanyFilterChange('country', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Countries</MenuItem>
                <MenuItem value="USA">USA</MenuItem>
                <MenuItem value="Canada">Canada</MenuItem>
                <MenuItem value="UK">UK</MenuItem>
                <MenuItem value="Germany">Germany</MenuItem>
                <MenuItem value="France">France</MenuItem>
                <MenuItem value="Australia">Australia</MenuItem>
                <MenuItem value="Japan">Japan</MenuItem>
              </TextField>
              <TextField
                label="Tier"
                select
                variant="outlined"
                size="small"
                value={companyFilters.tier}
                onChange={(e) => handleCompanyFilterChange('tier', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Tiers</MenuItem>
                <MenuItem value="ALL">ALL</MenuItem>
                <MenuItem value="BASIC">BASIC</MenuItem>
                <MenuItem value="PREMIUM">PREMIUM</MenuItem>
                <MenuItem value="ENTERPRISE">ENTERPRISE</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                onClick={clearCompanyFilters}
                disabled={!companyFilters.search && !companyFilters.country && !companyFilters.tier}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}

        {selectedTab === 'dealers' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Search"
                placeholder="Search by name, SF ID, city, or ASM owner..."
                variant="outlined"
                size="small"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                sx={{ minWidth: 250 }}
              />
              <TextField
                label="Manufacturer"
                select
                variant="outlined"
                size="small"
                value={filters.company_id}
                onChange={(e) => handleFilterChange('company_id', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Manufacturers</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={!filters.search && !filters.company_id}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}

        {selectedTab === 'rewards' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Search"
                placeholder="Search by reward name..."
                variant="outlined"
                size="small"
                value={rewardFilters.search}
                onChange={(e) => handleRewardFilterChange('search', e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Provider"
                select
                variant="outlined"
                size="small"
                value={rewardFilters.provider_id}
                onChange={(e) => handleRewardFilterChange('provider_id', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Providers</MenuItem>
                {rewardProviders.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Company"
                select
                variant="outlined"
                size="small"
                value={rewardFilters.company_id}
                onChange={(e) => handleRewardFilterChange('company_id', e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All companies</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={clearRewardFilters}
                disabled={!rewardFilters.search && !rewardFilters.provider_id && !rewardFilters.company_id}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}

        {selectedTab === 'logs' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Search"
                placeholder="Search by email or description..."
                variant="outlined"
                size="small"
                value={logFilters.search}
                onChange={(e) => handleLogFilterChange('search', e.target.value)}
                sx={{ minWidth: 250 }}
              />
              <TextField
                label="Manufacturer"
                select
                variant="outlined"
                size="small"
                value={logFilters.company_id}
                onChange={(e) => handleLogFilterChange('company_id', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Manufacturers</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Log Type"
                select
                variant="outlined"
                size="small"
                value={logFilters.log_type}
                onChange={(e) => handleLogFilterChange('log_type', e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="LOGIN">LOGIN</MenuItem>
                <MenuItem value="LOGOUT">LOGOUT</MenuItem>
                <MenuItem value="ERROR">ERROR</MenuItem>
                <MenuItem value="INFO">INFO</MenuItem>
                <MenuItem value="SYSTEM">SYSTEM</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={logFilters.log_type_negative || false}
                    onChange={(e) => handleLogFilterChange('log_type_negative', e.target.checked)}
                    disabled={!logFilters.log_type}
                    size="small"
                  />
                }
                label="Negative"
              />
              <Button
                variant="outlined"
                onClick={clearLogFilters}
                disabled={!logFilters.search && !logFilters.log_type && !logFilters.company_id && !logFilters.log_type_negative}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}

        {selectedTab === 'statistics' && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
            <Typography variant="h5" component="div" sx={{ mb: 3 }}>
              Statistics
            </Typography>
            <AdminDailyStatisticsView companies={companies} />
          </Paper>
        )}

        {selectedTab === 'settings' && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Settings
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Timezone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select your timezone for displaying dates and times across all views
              </Typography>
              <FormControl sx={{ minWidth: 300 }}>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={adminTimezone}
                  onChange={(e) => {
                    const newTimezone = e.target.value;
                    setAdminTimezone(newTimezone);
                    localStorage.setItem('adminTimezone', newTimezone);
                    setSuccess('Timezone updated successfully');
                  }}
                  label="Timezone"
                >
                  <MenuItem value="UTC">UTC (Coordinated Universal Time)</MenuItem>
                  <MenuItem value="Europe/Warsaw">Europe/Warsaw (Central European Time)</MenuItem>
                  <MenuItem value="Europe/London">Europe/London (GMT/BST)</MenuItem>
                  <MenuItem value="America/New_York">America/New_York (Eastern Time)</MenuItem>
                  <MenuItem value="America/Chicago">America/Chicago (Central Time)</MenuItem>
                  <MenuItem value="America/Denver">America/Denver (Mountain Time)</MenuItem>
                  <MenuItem value="America/Los_Angeles">America/Los_Angeles (Pacific Time)</MenuItem>
                  <MenuItem value="Asia/Tokyo">Asia/Tokyo (Japan Standard Time)</MenuItem>
                  <MenuItem value="Asia/Shanghai">Asia/Shanghai (China Standard Time)</MenuItem>
                  <MenuItem value="Asia/Singapore">Asia/Singapore (Singapore Time)</MenuItem>
                  <MenuItem value="Australia/Sydney">Australia/Sydney (Australian Eastern Time)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Current timezone: <strong>{adminTimezone}</strong>
              </Typography>
            </Box>
          </Paper>
        )}

        {selectedTab === 'sales' ? (
          <Paper sx={{ p: 3, minWidth: 0, maxWidth: '100%' }}>
            <Typography variant="h5" gutterBottom>
              Sales Communications
            </Typography>

            <Tabs value={salesTab} onChange={(e, v) => setSalesTab(v)} sx={{ mb: 3 }}>
              <Tab label="History" value="history" />
              <Tab label="Send" value="send" />
              <Tab label="Statistics" value="statistics" />
              <Tab label="Settings" value="settings" />
              <Tab label="CRM" value="crm" />
            </Tabs>

            {/* HISTORY TAB */}
            {salesTab === 'history' && (
              <Box>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Action Buttons */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {selectedSalesComms.length > 0 ? (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {selectedSalesComms.length} selected
                          </Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteBulkDialogOpen(true)}
                          >
                            Delete Selected
                          </Button>
                        </>
                      ) : (
                        <>
                          <Box />
                          <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={async () => {
                              setLoading(true);
                              const result = await apiCall('get', `/api/sales/history?page=${salesPage + 1}&per_page=${salesRowsPerPage}&sort_by=${salesSortBy}&sort_order=${salesSortOrder}`);
                              if (result.success) {
                                setSalesHistory(result.data.communications || []);
                                setSalesTotal(result.data.total || 0);
                              }
                              setLoading(false);
                            }}
                            disabled={loading}
                          >
                            Refresh
                          </Button>
                        </>
                      )}
                    </Box>

                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                indeterminate={selectedSalesComms.length > 0 && selectedSalesComms.length < salesHistory.length}
                                checked={salesHistory.length > 0 && selectedSalesComms.length === salesHistory.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSalesComms(salesHistory.map(comm => comm.id));
                                  } else {
                                    setSelectedSalesComms([]);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={salesSortBy === 'created'}
                                direction={salesSortBy === 'created' ? salesSortOrder : 'asc'}
                                onClick={() => handleSalesSort('created')}
                              >
                                Date
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={salesSortBy === 'title'}
                                direction={salesSortBy === 'title' ? salesSortOrder : 'asc'}
                                onClick={() => handleSalesSort('title')}
                              >
                                Title
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={salesSortBy === 'recipient_count'}
                                direction={salesSortBy === 'recipient_count' ? salesSortOrder : 'asc'}
                                onClick={() => handleSalesSort('recipient_count')}
                              >
                                Recipients
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={salesSortBy === 'delivered_count'}
                                direction={salesSortBy === 'delivered_count' ? salesSortOrder : 'asc'}
                                onClick={() => handleSalesSort('delivered_count')}
                              >
                                Delivered
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={salesSortBy === 'unique_opened_count'}
                                direction={salesSortBy === 'unique_opened_count' ? salesSortOrder : 'asc'}
                                onClick={() => handleSalesSort('unique_opened_count')}
                              >
                                Opened
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={salesSortBy === 'unique_clicked_count'}
                                direction={salesSortBy === 'unique_clicked_count' ? salesSortOrder : 'asc'}
                                onClick={() => handleSalesSort('unique_clicked_count')}
                              >
                                Clicked
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={salesSortBy === 'modified'}
                                direction={salesSortBy === 'modified' ? salesSortOrder : 'asc'}
                                onClick={() => handleSalesSort('modified')}
                              >
                                Modified
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {salesHistory.map((comm) => {
                            const isSelected = selectedSalesComms.includes(comm.id);
                            return (
                              <TableRow key={comm.id} sx={{ backgroundColor: '#fff' }}>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedSalesComms([...selectedSalesComms, comm.id]);
                                      } else {
                                        setSelectedSalesComms(selectedSalesComms.filter(id => id !== comm.id));
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>{formatDateWithTimezone(comm.created)}</TableCell>
                                <TableCell>{comm.title}</TableCell>
                                <TableCell>{comm.recipient_count}</TableCell>
                                <TableCell>{comm.delivered_count}</TableCell>
                                <TableCell>{comm.unique_opened_count}</TableCell>
                                <TableCell>{comm.unique_clicked_count}</TableCell>
                                <TableCell>{formatDateWithTimezone(comm.modified)}</TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      setLoading(true);
                                      const result = await apiCall('get', `/api/sales/communication/${comm.id}`);
                                      setLoading(false);
                                      if (result.success) {
                                        setSelectedComm(result.data);
                                        setViewCommDialogOpen(true);
                                      } else {
                                        setError(result.error);
                                      }
                                    }}
                                    title="View details"
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      // Copy message data to SEND form (without recipients)
                                      setSalesFormData({
                                        ...salesFormData,
                                        title: comm.title || '',
                                        subtitle: comm.subtitle || '',
                                        content: comm.content || '',
                                        extra_content: comm.extra_content || '',
                                        custom_cta_title: comm.custom_cta_title || '',
                                        custom_cta_url: comm.custom_cta_url || '',
                                        email_type: (comm.email_type === 'template' ? 'template_no_cta' : comm.email_type) || 'template_no_cta'
                                      });
                                      setSalesTab('send');
                                      setSuccess('Message copied to SEND form');
                                    }}
                                    title="Copy to SEND"
                                  >
                                    <ContentCopyIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setCommToDelete(comm);
                                      setDeleteCommDialogOpen(true);
                                    }}
                                    title="Delete"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {salesHistory.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={9} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No sales communications sent yet
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[25, 50, 100]}
                      component="div"
                      count={salesTotal}
                      rowsPerPage={salesRowsPerPage}
                      page={salesPage}
                      onPageChange={(e, newPage) => setSalesPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setSalesRowsPerPage(parseInt(e.target.value, 10));
                        setSalesPage(0);
                      }}
                    />
                  </>
                )}
              </Box>
            )}

            {/* SEND TAB */}
            {salesTab === 'send' && (
              <Box>
                {/* Recipients Section */}
                <Typography variant="h6" gutterBottom>Recipients</Typography>
                <Box sx={{ mb: 3 }}>
                  <input
                    accept=".csv"
                    style={{ display: 'none' }}
                    id="csv-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const inputElement = e.target;
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const text = event.target.result;
                          const lines = text.split('\n');
                          if (lines.length < 2) {
                            setError('CSV file is empty or invalid');
                            inputElement.value = '';
                            return;
                          }

                          const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
                          const emailIndex = headers.findIndex(h => h.includes('email'));
                          const firstNameIndex = headers.findIndex(h => h.includes('first') && h.includes('name'));
                          const lastNameIndex = headers.findIndex(h => h.includes('last') && h.includes('name'));
                          const companyIndex = headers.findIndex(h => h.includes('company'));
                          const titleIndex = headers.findIndex(h => h.includes('title'));

                          if (emailIndex === -1) {
                            setError('CSV must have an "Email" column');
                            return;
                          }

                          const recipients = [];
                          const seenEmails = new Set();
                          let invalidCount = 0;
                          let duplicateCount = 0;
                          const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

                          for (let i = 1; i < lines.length; i++) {
                            const line = lines[i].trim();
                            if (!line) continue;

                            const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                            const rawEmail = values[emailIndex] || '';
                            const emails = parseEmailField(rawEmail);

                            // Count invalid parts (for feedback)
                            if (rawEmail) {
                              const parts = rawEmail.split(';').map(p => p.trim()).filter(Boolean);
                              invalidCount += parts.filter(p => !emailPattern.test(p)).length;
                            }

                            if (emails.length === 0) continue;

                            const baseRecipient = {
                              first_name: firstNameIndex >= 0 ? values[firstNameIndex] || '' : '',
                              last_name: lastNameIndex >= 0 ? values[lastNameIndex] || '' : '',
                              company_name: companyIndex >= 0 ? values[companyIndex] || '' : '',
                              title: titleIndex >= 0 ? values[titleIndex] || '' : ''
                            };

                            for (const email of emails) {
                              const emailLower = email.toLowerCase();
                              if (seenEmails.has(emailLower)) {
                                duplicateCount++;
                                continue;
                              }
                              seenEmails.add(emailLower);
                              recipients.push({ ...baseRecipient, email });
                            }
                          }

                          setSalesRecipients(recipients);
                          setSuccess(`Loaded ${recipients.length} recipients. Skipped: ${invalidCount} invalid, ${duplicateCount} duplicates`);
                          inputElement.value = '';
                        };
                        reader.readAsText(file);
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label htmlFor="csv-upload">
                      <Button variant="outlined" component="span">
                        Upload CSV
                      </Button>
                    </label>
                    {salesRecipients.length > 0 && (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={selectedRecipients.length === 0}
                          onClick={() => {
                            setSalesRecipients(salesRecipients.filter((_, idx) => !selectedRecipients.includes(idx)));
                            setSelectedRecipients([]);
                          }}
                        >
                          Delete Selected ({selectedRecipients.length})
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => {
                            setSalesRecipients([]);
                            setSelectedRecipients([]);
                          }}
                        >
                          Clear All
                        </Button>
                      </>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {salesRecipients.length} recipients loaded
                  </Typography>
                </Box>

                {salesRecipients.length > 0 && (
                  <Box sx={{ mb: 3, maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedRecipients.length > 0 && selectedRecipients.length < salesRecipients.length}
                              checked={salesRecipients.length > 0 && selectedRecipients.length === salesRecipients.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRecipients(salesRecipients.map((_, idx) => idx));
                                } else {
                                  setSelectedRecipients([]);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>First Name</TableCell>
                          <TableCell>Last Name</TableCell>
                          <TableCell>Company</TableCell>
                          <TableCell>Title</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {salesRecipients.map((r, idx) => (
                          <TableRow key={idx} sx={{ backgroundColor: '#fff' }}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedRecipients.includes(idx)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedRecipients([...selectedRecipients, idx]);
                                  } else {
                                    setSelectedRecipients(selectedRecipients.filter(i => i !== idx));
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>{r.email}</TableCell>
                            <TableCell>{r.first_name}</TableCell>
                            <TableCell>{r.last_name}</TableCell>
                            <TableCell>{r.company_name}</TableCell>
                            <TableCell>{r.title}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Message Section */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>Message</Typography>

                {/* Email Type Dropdown */}
                <FormControl sx={{ mb: 2, minWidth: 200 }}>
                  <InputLabel id="email-type-label">Email Type</InputLabel>
                  <Select
                    labelId="email-type-label"
                    value={salesFormData.email_type}
                    label="Email Type"
                    onChange={(e) => setSalesFormData({ ...salesFormData, email_type: e.target.value })}
                    sx={{ backgroundColor: '#fff' }}
                  >
                    <MenuItem value="template_no_cta">TEMPLATE NO CTA</MenuItem>
                    <MenuItem value="plain_text">PLAIN TEXT</MenuItem>
                  </Select>
                  {salesFormData.email_type === 'plain_text' && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                      Note: Plain text emails do not support open tracking
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  label="Title (Email Subject)"
                  value={salesFormData.title}
                  onChange={(e) => setSalesFormData({ ...salesFormData, title: e.target.value })}
                  required
                  sx={{ mb: 2, backgroundColor: '#fff' }}
                />

                {/* Subtitle - only for template emails */}
                {salesFormData.email_type === 'template' && (
                  <TextField
                    fullWidth
                    label="Subtitle"
                    value={salesFormData.subtitle}
                    onChange={(e) => setSalesFormData({ ...salesFormData, subtitle: e.target.value })}
                    sx={{ mb: 2, backgroundColor: '#fff' }}
                  />
                )}

                {/* Content - RichTextEditor for template and template_no_cta, plain TextField for plain_text */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Content *</Typography>
                  {salesFormData.email_type === 'template' || salesFormData.email_type === 'template_no_cta' ? (
                    <RichTextEditor
                      value={salesFormData.content}
                      onChange={(value) => setSalesFormData({ ...salesFormData, content: value })}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={salesFormData.content}
                      onChange={(e) => setSalesFormData({ ...salesFormData, content: e.target.value })}
                      placeholder="Enter plain text email content. You can use placeholders: <First Name>, <Last Name>, <Company>, <Title>"
                      sx={{ backgroundColor: '#fff' }}
                    />
                  )}
                </Box>

                {/* Extra Content - only for template emails */}
                {salesFormData.email_type === 'template' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Extra Content (below CTA)</Typography>
                    <RichTextEditor
                      value={salesFormData.extra_content}
                      onChange={(value) => setSalesFormData({ ...salesFormData, extra_content: value })}
                    />
                  </Box>
                )}

                {/* Custom CTA Button - only for template emails */}
                {salesFormData.email_type === 'template' && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Custom CTA Button (Required)</Typography>
                    <TextField
                      fullWidth
                      label="CTA Button Title"
                      value={salesFormData.custom_cta_title}
                      onChange={(e) => setSalesFormData({ ...salesFormData, custom_cta_title: e.target.value })}
                      required
                      sx={{ mb: 2, backgroundColor: '#fff' }}
                    />
                    <TextField
                      fullWidth
                      label="CTA Button URL"
                      value={salesFormData.custom_cta_url}
                      onChange={(e) => setSalesFormData({ ...salesFormData, custom_cta_url: e.target.value })}
                      required
                      sx={{ mb: 2, backgroundColor: '#fff' }}
                    />
                  </>
                )}

                {/* Send Section */}
                {/* Send Section */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    label="Test Email Address"
                    value={salesFormData.test_email}
                    onChange={(e) => setSalesFormData({ ...salesFormData, test_email: e.target.value })}
                    sx={{ flex: 1, backgroundColor: '#fff' }}
                  />
                  <Button
                    variant="outlined"
                    disabled={
                      !salesFormData.test_email ||
                      !salesFormData.title ||
                      !salesFormData.content ||
                      (salesFormData.email_type === 'template' && (!salesFormData.custom_cta_title || !salesFormData.custom_cta_url))
                    }
                    onClick={async () => {
                      setLoading(true);
                      const result = await apiCall('post', '/api/sales/send-test', {
                        title: salesFormData.title,
                        subtitle: salesFormData.subtitle,
                        content: salesFormData.content,
                        extra_content: salesFormData.extra_content,
                        custom_cta_title: salesFormData.custom_cta_title,
                        custom_cta_url: salesFormData.custom_cta_url,
                        email_type: salesFormData.email_type,
                        recipients: [{
                          email: salesFormData.test_email,
                          first_name: 'Test',
                          last_name: 'User',
                          company_name: '',
                          title: ''
                        }]
                      });
                      setLoading(false);
                      if (result.success) {
                        setSuccess(`Test email sent to ${salesFormData.test_email}`);
                      } else {
                        setError(result.error);
                      }
                    }}
                  >
                    Send Test
                  </Button>
                  <FormControl sx={{ minWidth: 140 }} size="small">
                    <InputLabel id="send-status-label">Set status to</InputLabel>
                    <Select
                      labelId="send-status-label"
                      value={salesFormData.send_status}
                      label="Set status to"
                      onChange={(e) => setSalesFormData({ ...salesFormData, send_status: e.target.value })}
                      sx={{ backgroundColor: '#fff' }}
                    >
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="email_2">Email 2</MenuItem>
                      <MenuItem value="email_3">Email 3</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      !salesRecipients.length ||
                      !salesFormData.title ||
                      !salesFormData.content ||
                      (salesFormData.email_type === 'template' && (!salesFormData.custom_cta_title || !salesFormData.custom_cta_url))
                    }
                    onClick={() => setSendConfirmOpen(true)}
                  >
                    SEND NOW
                  </Button>
                </Box>

                {/* Send Confirmation Dialog */}
                <Dialog open={sendConfirmOpen} onClose={() => !sendingEmails && setSendConfirmOpen(false)}>
                  <DialogTitle>Confirm Send</DialogTitle>
                  <DialogContent>
                    {sendingEmails ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
                        <CircularProgress />
                        <Typography>Sending emails...</Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography>
                          Send {salesFormData.email_type === 'plain_text' ? 'plain text' : salesFormData.email_type === 'template_no_cta' ? 'template (no CTA)' : 'template'} email to {salesRecipients.length} recipient(s)?
                        </Typography>
                        {salesFormData.email_type === 'plain_text' && (
                          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                            Note: Plain text emails do not support open tracking.
                          </Typography>
                        )}
                      </>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSendConfirmOpen(false)} disabled={sendingEmails}>Cancel</Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={sendingEmails}
                      onClick={async () => {
                        setSendingEmails(true);
                        try {
                          const result = await apiCall('post', '/api/sales/send', {
                            title: salesFormData.title,
                            subtitle: salesFormData.subtitle,
                            content: salesFormData.content,
                            extra_content: salesFormData.extra_content,
                            custom_cta_title: salesFormData.custom_cta_title,
                            custom_cta_url: salesFormData.custom_cta_url,
                            email_type: salesFormData.email_type,
                            recipients: salesRecipients,
                            update_status: salesFormData.send_status
                          });
                          setSendConfirmOpen(false);
                          if (result.success) {
                            setSuccess(`Successfully sent to ${result.data.total_sent} recipients`);
                            setSalesRecipients([]);
                            // Load settings to get default CTA for next send
                            const settingsResult = await apiCall('get', '/api/sales/settings');
                            const defaultCta = settingsResult.success ? settingsResult.data : {};
                            setSalesFormData({
                              title: '',
                              subtitle: '',
                              content: '',
                              extra_content: '',
                              custom_cta_title: defaultCta.default_cta_title || '',
                              custom_cta_url: defaultCta.default_cta_url || '',
                              test_email: '',
                              email_type: 'template_no_cta',
                              send_status: 'email'
                            });
                            setSalesTab('history');
                          } else {
                            setError(result.error);
                          }
                        } finally {
                          setSendingEmails(false);
                        }
                      }}
                    >
                      Confirm
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}

            {/* STATISTICS TAB */}
            {salesTab === 'statistics' && (
              <Box>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : salesStatistics ? (
                  <Box>
                    {/* Refresh Button */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={async () => {
                          setLoading(true);
                          const result = await apiCall('get', '/api/sales/statistics');
                          if (result.success) {
                            setSalesStatistics(result.data);
                          }
                          setLoading(false);
                        }}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Total Communications</Typography>
                            <Typography variant="h4">{salesStatistics.total_communications}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Total Recipients</Typography>
                            <Typography variant="h4">{salesStatistics.total_recipients}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Delivery Rate</Typography>
                            <Typography variant="h4">{salesStatistics.delivery_rate}%</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Open Rate</Typography>
                            <Typography variant="h4">{salesStatistics.open_rate}%</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Click Rate</Typography>
                            <Typography variant="h4">{salesStatistics.click_rate}%</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Bounce Rate</Typography>
                            <Typography variant="h4">{salesStatistics.bounce_rate}%</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Delivered</Typography>
                            <Typography variant="h4">{salesStatistics.total_delivered}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ backgroundColor: '#fff' }}>
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>Unique Opens</Typography>
                            <Typography variant="h4">{salesStatistics.unique_opened}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Typography>No statistics available</Typography>
                )}
              </Box>
            )}

            {/* SETTINGS TAB */}
            {salesTab === 'settings' && (
              <Box>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : salesSettings ? (
                  <Box>
                    <Typography variant="h6" gutterBottom>Email Configuration</Typography>
                    <TextField
                      fullWidth
                      label="Email From Name"
                      value={salesSettings.email_from_name || ''}
                      onChange={(e) => setSalesSettings({ ...salesSettings, email_from_name: e.target.value })}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Email From Address"
                      value={salesSettings.email_from_address || ''}
                      onChange={(e) => setSalesSettings({ ...salesSettings, email_from_address: e.target.value })}
                      sx={{ mb: 2 }}
                    />

                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Default CTA Button</Typography>
                    <TextField
                      fullWidth
                      label="Default CTA Button Title"
                      value={salesSettings.default_cta_title || ''}
                      onChange={(e) => setSalesSettings({ ...salesSettings, default_cta_title: e.target.value })}
                      sx={{ mb: 2 }}
                      helperText="This will auto-populate in the SEND tab"
                    />
                    <TextField
                      fullWidth
                      label="Default CTA Button URL"
                      value={salesSettings.default_cta_url || ''}
                      onChange={(e) => setSalesSettings({ ...salesSettings, default_cta_url: e.target.value })}
                      sx={{ mb: 2 }}
                      helperText="This will auto-populate in the SEND tab"
                    />

                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Default Email Header Image</Typography>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="header-image-upload"
                      type="file"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const uploadFormData = new FormData();
                          uploadFormData.append('file', file);
                          setLoading(true);
                          const response = await fetch(`${API_URL}/api/upload/sales-header-image`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                            body: uploadFormData,
                          });
                          setLoading(false);
                          if (response.ok) {
                            const result = await response.json();
                            setSalesSettings({ ...salesSettings, email_header_image: result.public_url });
                            setSuccess('Header image uploaded');
                          } else {
                            setError('Failed to upload header image');
                          }
                        }
                      }}
                    />
                    <label htmlFor="header-image-upload">
                      <Button variant="outlined" component="span" sx={{ mb: 2 }}>
                        Upload Header Image
                      </Button>
                    </label>
                    {salesSettings.email_header_image && (
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <img src={salesSettings.email_header_image} alt="Header" style={{ maxWidth: 200, maxHeight: 100 }} />
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={async () => {
                            setLoading(true);
                            const updatedSettings = { ...salesSettings, email_header_image: '' };
                            const result = await apiCall('put', '/api/sales/settings', updatedSettings);
                            setLoading(false);
                            if (result.success) {
                              setSalesSettings(updatedSettings);
                              setSuccess('Header image deleted');
                            } else {
                              setError(result.error || 'Failed to delete header image');
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}

                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Default Email Footer Image</Typography>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="footer-image-upload"
                      type="file"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const uploadFormData = new FormData();
                          uploadFormData.append('file', file);
                          setLoading(true);
                          const response = await fetch(`${API_URL}/api/upload/sales-footer-image`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                            body: uploadFormData,
                          });
                          setLoading(false);
                          if (response.ok) {
                            const result = await response.json();
                            setSalesSettings({ ...salesSettings, email_footer_image: result.public_url });
                            setSuccess('Footer image uploaded');
                          } else {
                            setError('Failed to upload footer image');
                          }
                        }
                      }}
                    />
                    <label htmlFor="footer-image-upload">
                      <Button variant="outlined" component="span" sx={{ mb: 2 }}>
                        Upload Footer Image
                      </Button>
                    </label>
                    {salesSettings.email_footer_image && (
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <img src={salesSettings.email_footer_image} alt="Footer" style={{ maxWidth: 200, maxHeight: 100 }} />
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={async () => {
                            setLoading(true);
                            const updatedSettings = { ...salesSettings, email_footer_image: '' };
                            const result = await apiCall('put', '/api/sales/settings', updatedSettings);
                            setLoading(false);
                            if (result.success) {
                              setSalesSettings(updatedSettings);
                              setSuccess('Footer image deleted');
                            } else {
                              setError(result.error || 'Failed to delete footer image');
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}

                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Default Email Footer</Typography>
                    <RichTextEditor
                      value={salesSettings.email_footer || ''}
                      onChange={(value) => setSalesSettings({ ...salesSettings, email_footer: value })}
                    />

                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mt: 3 }}
                      onClick={async () => {
                        setLoading(true);
                        const result = await apiCall('put', '/api/sales/settings', salesSettings);
                        setLoading(false);
                        if (result.success) {
                          setSuccess('Settings saved successfully');
                        } else {
                          setError(result.error);
                        }
                      }}
                    >
                      Save Settings
                    </Button>
                  </Box>
                ) : (
                  <Typography>Loading settings...</Typography>
                )}
              </Box>
            )}

            {/* CRM TAB */}
            {salesTab === 'crm' && (
              <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <Tabs value={crmTab} onChange={(e, v) => {
                  setCrmTab(v);
                  setSelectedCrmCompanies(new Set());
                  setSelectedCrmContacts(new Set());
                }} sx={{ mb: 3 }}>
                  <Tab label="Companies" value="companies" />
                  <Tab label="Contacts" value="contacts" />
                  <Tab label="Statistics" value="statistics" />
                </Tabs>

                {/* COMPANIES VIEW */}
                {crmTab === 'companies' && (
                  <Box>
                    {/* Filters and Actions */}
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        label="Search"
                        size="small"
                        value={crmSearchFilter}
                        onChange={(e) => {
                          setCrmSearchFilter(e.target.value);
                          setSelectedCrmCompanies(new Set());
                        }}
                        sx={{ minWidth: 200 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={crmStatusFilter}
                          label="Status"
                          onChange={(e) => {
                            setCrmStatusFilter(e.target.value);
                            setSelectedCrmCompanies(new Set());
                            setCrmCompaniesPage(0); // Reset to first page when filter changes
                          }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="email">Email</MenuItem>
                          <MenuItem value="email_2">Email 2</MenuItem>
                          <MenuItem value="email_3">Email 3</MenuItem>
                          <MenuItem value="phone">Phone</MenuItem>
                          <MenuItem value="reply">Reply</MenuItem>
                          <MenuItem value="won">Won</MenuItem>
                          <MenuItem value="lost">Lost</MenuItem>
                          <MenuItem value="wrong">Wrong</MenuItem>
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="click">Click</MenuItem>
                          <MenuItem value="followup">Followup</MenuItem>
                          <MenuItem value="demo">Demo</MenuItem>
                          <MenuItem value="priority">Priority</MenuItem>
                          <MenuItem value="unsubscribe">Unsubscribe</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={crmCompaniesPhoneFilter}
                            onChange={(e) => {
                              setCrmCompaniesPhoneFilter(e.target.checked);
                              setSelectedCrmCompanies(new Set());
                              setCrmCompaniesPage(0);
                            }}
                          />
                        }
                        label="Phone"
                        sx={{ ml: 1 }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setCrmSearchFilter('');
                          setCrmStatusFilter('');
                          setCrmCompaniesPhoneFilter(false);
                          setSelectedCrmCompanies(new Set());
                          setCrmCompaniesPage(0);
                        }}
                        disabled={!crmSearchFilter && !crmStatusFilter && !crmCompaniesPhoneFilter}
                        sx={{ mr: 1 }}
                      >
                        Clear Filters
                      </Button>
                      <Box sx={{ flexGrow: 1 }} />
                      {selectedCrmCompanies.size > 0 && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAddCompaniesToSend}
                          disabled={loading}
                          sx={{ mr: 1 }}
                        >
                          Add to SEND ({selectedCrmCompanies.size})
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setEditingCrmCompany(null);
                          setCrmCompanyFormData({
                            name: '',
                            www: '',
                            linkedin: '',
                            email: '',
                            phone: '',
                            industry: '',
                            time_zone: '',
                            status: 'none'
                          });
                          setCrmCompanyDialogOpen(true);
                        }}
                      >
                        Add Company
                      </Button>
                    </Box>

                    {/* Companies Table */}
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                      indeterminate={selectedCrmCompanies.size > 0 && selectedCrmCompanies.size < crmCompanies.length}
                                      checked={crmCompanies.length > 0 && selectedCrmCompanies.size === crmCompanies.length}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedCrmCompanies(new Set(crmCompanies.map(c => c.id)));
                                        } else {
                                          setSelectedCrmCompanies(new Set());
                                        }
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={(e) => setCrmCompanyMenuAnchor(e.currentTarget)}
                                      sx={{ ml: 0.5 }}
                                    >
                                      <SettingsIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  <Menu
                                    anchorEl={crmCompanyMenuAnchor}
                                    open={Boolean(crmCompanyMenuAnchor)}
                                    onClose={() => setCrmCompanyMenuAnchor(null)}
                                  >
                                    <MenuItem onClick={() => {
                                      setSelectedCrmCompanies(new Set(crmCompanies.map(c => c.id)));
                                      setCrmCompanyMenuAnchor(null);
                                    }}>
                                      Select All
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                      const everySecond = new Set();
                                      crmCompanies.forEach((company, idx) => {
                                        if (idx % 2 === 0) {
                                          everySecond.add(company.id);
                                        }
                                      });
                                      setSelectedCrmCompanies(everySecond);
                                      setCrmCompanyMenuAnchor(null);
                                    }}>
                                      Select Every Second Row
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                      setSelectedCrmCompanies(new Set());
                                      setCrmCompanyMenuAnchor(null);
                                    }}>
                                      Clear Selection
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={crmCompaniesSortBy === 'name'}
                                    direction={crmCompaniesSortBy === 'name' ? crmCompaniesSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmCompaniesSortBy === 'name') {
                                        setCrmCompaniesSortOrder(crmCompaniesSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmCompaniesSortBy('name');
                                        setCrmCompaniesSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Name
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={crmCompaniesSortBy === 'industry'}
                                    direction={crmCompaniesSortBy === 'industry' ? crmCompaniesSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmCompaniesSortBy === 'industry') {
                                        setCrmCompaniesSortOrder(crmCompaniesSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmCompaniesSortBy('industry');
                                        setCrmCompaniesSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Industry
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={crmCompaniesSortBy === 'phone'}
                                    direction={crmCompaniesSortBy === 'phone' ? crmCompaniesSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmCompaniesSortBy === 'phone') {
                                        setCrmCompaniesSortOrder(crmCompaniesSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmCompaniesSortBy('phone');
                                        setCrmCompaniesSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Phone
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={crmCompaniesSortBy === 'status'}
                                    direction={crmCompaniesSortBy === 'status' ? crmCompaniesSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmCompaniesSortBy === 'status') {
                                        setCrmCompaniesSortOrder(crmCompaniesSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmCompaniesSortBy('status');
                                        setCrmCompaniesSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Status
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={crmCompaniesSortBy === 'contacts_count'}
                                    direction={crmCompaniesSortBy === 'contacts_count' ? crmCompaniesSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmCompaniesSortBy === 'contacts_count') {
                                        setCrmCompaniesSortOrder(crmCompaniesSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmCompaniesSortBy('contacts_count');
                                        setCrmCompaniesSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Contacts
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={crmCompaniesSortBy === 'modified'}
                                    direction={crmCompaniesSortBy === 'modified' ? crmCompaniesSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmCompaniesSortBy === 'modified') {
                                        setCrmCompaniesSortOrder(crmCompaniesSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmCompaniesSortBy('modified');
                                        setCrmCompaniesSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Modified
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {crmCompanies.map((company) => (
                                <TableRow key={company.id} sx={{ backgroundColor: '#fff' }}>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selectedCrmCompanies.has(company.id)}
                                      onChange={(e) => {
                                        const newSet = new Set(selectedCrmCompanies);
                                        if (e.target.checked) {
                                          newSet.add(company.id);
                                        } else {
                                          newSet.delete(company.id);
                                        }
                                        setSelectedCrmCompanies(newSet);
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>{company.name}</TableCell>
                                  <TableCell>{company.industry || '-'}</TableCell>
                                  <TableCell>{company.phone || '-'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={companyStatusUpdatingId === company.id ? '...' : company.status}
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCompanyStatusMenuAnchor(e.currentTarget);
                                        setCompanyStatusMenuCompanyId(company.id);
                                      }}
                                      sx={{
                                        cursor: 'pointer',
                                        backgroundColor:
                                          company.status === 'email' ? '#ff9800' :
                                            company.status === 'email_2' ? '#ffb74d' :
                                              company.status === 'email_3' ? '#ffcc80' :
                                                company.status === 'phone' ? '#ffeb3b' :
                                                  company.status === 'reply' ? '#2196f3' :
                                                    company.status === 'won' ? '#4caf50' :
                                                      company.status === 'lost' ? '#e91e63' :
                                                        company.status === 'wrong' ? '#f44336' :
                                                          company.status === 'open' ? '#F3E5AB' :
                                                            company.status === 'click' ? '#8bc34a' : // New color for 'click'
                                                              company.status === 'followup' ? '#9c27b0' :
                                                                company.status === 'demo' ? '#00bcd4' :
                                                                  company.status === 'priority' ? '#e65100' :
                                                                    company.status === 'unsubscribe' ? '#6366f1' :
                                                                      '#e0e0e0',
                                        color:
                                          company.status === 'phone' ? '#000' :
                                            company.status === 'open' ? '#000' :
                                              company.status === 'click' ? '#000' : // New color for 'click'
                                                company.status === 'email' || company.status === 'email_2' || company.status === 'email_3' || company.status === 'won' || company.status === 'lost' || company.status === 'reply' || company.status === 'wrong' || company.status === 'followup' || company.status === 'demo' || company.status === 'priority' || company.status === 'unsubscribe' ? '#fff' :
                                                  '#000'
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>{company.contacts_count || 0}</TableCell>
                                  <TableCell>{formatDateWithTimezone(company.modified)}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewCompanyContacts(company.id)}
                                      title="View Contacts"
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => openCrmCompanyEdit(company)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={async () => {
                                        if (window.confirm(`Delete company "${company.name}"?`)) {
                                          setLoading(true);
                                          const result = await apiCall('delete', `/api/sales/crm/companies/${company.id}`);
                                          setLoading(false);
                                          if (result.success) {
                                            setSuccess('Company deleted');
                                            // Reload companies
                                            let reloadUrl = `/api/sales/crm/companies?page=${crmCompaniesPage + 1}&per_page=${crmCompaniesRowsPerPage}`;
                                            if (crmStatusFilter) reloadUrl += `&status_filter=${crmStatusFilter}`;
                                            if (crmSearchFilter) reloadUrl += `&search=${encodeCrmSearchParam(crmSearchFilter)}`;
                                            if (crmCompaniesPhoneFilter) reloadUrl += `&has_phone=true`;
                                            reloadUrl += `&sort_by=${crmCompaniesSortBy}&sort_order=${crmCompaniesSortOrder}`;
                                            const reloadResult = await apiCall('get', reloadUrl);
                                            if (reloadResult.success && reloadResult.data) {
                                              setCrmCompanies(reloadResult.data.companies || []);
                                              setCrmCompaniesTotal(reloadResult.data.total || 0);
                                            }
                                          } else {
                                            setError(result.error || 'Failed to delete company');
                                          }
                                        }
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Menu
                          anchorEl={companyStatusMenuAnchor}
                          open={Boolean(companyStatusMenuAnchor)}
                          onClose={() => {
                            setCompanyStatusMenuAnchor(null);
                            setCompanyStatusMenuCompanyId(null);
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          MenuListProps={{ sx: { py: 0 } }}
                        >
                          {(() => {
                            const company = crmCompanies.find(c => c.id === companyStatusMenuCompanyId);
                            return ['none', 'email', 'email_2', 'email_3', 'phone', 'reply', 'won', 'lost', 'wrong', 'open', 'click', 'followup', 'demo', 'priority', 'unsubscribe'].map((statusOption) => (
                              <MenuItem
                                key={statusOption}
                                selected={company?.status === statusOption}
                                onClick={() => handleInlineCompanyStatusChange(companyStatusMenuCompanyId, statusOption)}
                              >
                                {statusOption.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase())}
                              </MenuItem>
                            ));
                          })()}
                        </Menu>
                        <TablePagination
                          component="div"
                          count={crmCompaniesTotal}
                          page={crmCompaniesPage}
                          onPageChange={(e, newPage) => setCrmCompaniesPage(newPage)}
                          rowsPerPage={crmCompaniesRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setCrmCompaniesRowsPerPage(parseInt(e.target.value, 10));
                            setCrmCompaniesPage(0);
                          }}
                          rowsPerPageOptions={[25, 50, 100]}
                        />
                      </>
                    )}
                  </Box>
                )}

                {/* CONTACTS VIEW */}
                {crmTab === 'contacts' && (
                  <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                    {/* Filters (row 1) + actions (row 2) so buttons are not clipped */}
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, maxWidth: '100%' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                      <TextField
                        label="Search"
                        placeholder="Name, email, title, notes, LinkedIn…"
                        size="small"
                        value={crmSearchFilter}
                        onChange={(e) => {
                          setCrmSearchFilter(e.target.value);
                          setSelectedCrmContacts(new Set());
                        }}
                        sx={{ minWidth: 160, flex: '1 1 160px', maxWidth: 280 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 140, flex: '1 1 140px', maxWidth: 220 }}>
                        <InputLabel>Company</InputLabel>
                        <Select
                          value={crmCompanyFilter}
                          label="Company"
                          onChange={(e) => {
                            setCrmCompanyFilter(e.target.value);
                            setSelectedCrmContacts(new Set());
                          }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {crmCompaniesList.map((company) => (
                            <MenuItem key={company.id} value={company.id}>
                              {company.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px', maxWidth: 240 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          multiple
                          value={crmContactsStatusFilter}
                          label="Status"
                          onChange={(e) => {
                            setCrmContactsStatusFilter(e.target.value);
                            setSelectedCrmContacts(new Set());
                            setCrmContactsPage(0); // Reset to first page when filter changes
                          }}
                          renderValue={(selected) => selected.length ? selected.join(', ') : 'All'}
                        >
                          <MenuItem value="none">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('none') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="None" />
                          </MenuItem>
                          <MenuItem value="email">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('email') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Email" />
                          </MenuItem>
                          <MenuItem value="email_2">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('email_2') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Email 2" />
                          </MenuItem>
                          <MenuItem value="email_3">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('email_3') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Email 3" />
                          </MenuItem>
                          <MenuItem value="phone">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('phone') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Phone" />
                          </MenuItem>
                          <MenuItem value="reply">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('reply') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Reply" />
                          </MenuItem>
                          <MenuItem value="won">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('won') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Won" />
                          </MenuItem>
                          <MenuItem value="lost">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('lost') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Lost" />
                          </MenuItem>
                          <MenuItem value="wrong">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('wrong') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Wrong" />
                          </MenuItem>
                          <MenuItem value="open">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('open') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Open" />
                          </MenuItem>
                          <MenuItem value="click">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('click') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Click" />
                          </MenuItem>
                          <MenuItem value="followup">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('followup') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Followup" />
                          </MenuItem>
                          <MenuItem value="demo">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('demo') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Demo" />
                          </MenuItem>
                          <MenuItem value="priority">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('priority') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Priority" />
                          </MenuItem>
                          <MenuItem value="unsubscribe">
                            <Checkbox checked={crmContactsStatusFilter.indexOf('unsubscribe') > -1} sx={{ pointerEvents: 'none' }} />
                            <ListItemText primary="Unsubscribe" />
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150, flex: '1 1 150px', maxWidth: 220 }}>
                        <InputLabel>Score</InputLabel>
                        <Select
                          multiple
                          value={crmContactsScoreFilter}
                          label="Score"
                          onChange={(e) => {
                            const v = e.target.value;
                            const next = typeof v === 'string' ? [] : v;
                            const nums = next.map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10))).filter((n) => !Number.isNaN(n));
                            setCrmContactsScoreFilter(nums);
                            setSelectedCrmContacts(new Set());
                            setCrmContactsPage(0);
                          }}
                          renderValue={(selected) => {
                            if (!crmProspectScoreFilterRestricts(selected)) return 'All';
                            const sorted = [...selected].sort((a, b) => a - b);
                            if (sorted.length <= 4) return sorted.join(', ');
                            return `${sorted.length} selected`;
                          }}
                        >
                          {CRM_PROSPECT_SCORE_VALUES.map((score) => (
                            <MenuItem key={score} value={score}>
                              <Checkbox checked={crmContactsScoreFilter.indexOf(score) > -1} sx={{ pointerEvents: 'none' }} />
                              <ListItemText primary={String(score)} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={crmPhoneFilter}
                            onChange={(e) => {
                              setCrmPhoneFilter(e.target.checked);
                              setSelectedCrmContacts(new Set());
                              setCrmContactsPage(0); // Reset to first page when filter changes
                            }}
                          />
                        }
                        label="Phone"
                        sx={{ ml: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={crmNotesFilter}
                            onChange={(e) => {
                              setCrmNotesFilter(e.target.checked);
                              setSelectedCrmContacts(new Set());
                              setCrmContactsPage(0); // Reset to first page when filter changes
                            }}
                          />
                        }
                        label="Notes"
                        sx={{ ml: 0 }}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => {
                          setCrmSearchFilter('');
                          setCrmCompanyFilter('');
                          setCrmContactsStatusFilter([]);
                          setCrmPhoneFilter(false);
                          setCrmNotesFilter(false);
                          setCrmContactsScoreFilter([...CRM_PROSPECT_SCORE_VALUES]);
                          setCrmContactsIdFilter(null);
                          setSelectedCrmContacts(new Set());
                          setCrmContactsPage(0);
                        }}
                        disabled={
                          !crmSearchFilter &&
                          !crmCompanyFilter &&
                          crmContactsStatusFilter.length === 0 &&
                          !crmPhoneFilter &&
                          !crmNotesFilter &&
                          !crmProspectScoreFilterRestricts(crmContactsScoreFilter) &&
                          !(crmContactsIdFilter && crmContactsIdFilter.length > 0)
                        }
                      >
                        Clear Filters
                      </Button>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 1,
                          justifyContent: 'space-between',
                          rowGap: 1,
                          minWidth: 0,
                          maxWidth: '100%',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', minWidth: 0, flex: '1 1 auto' }}>
                          {selectedCrmContacts.size > 0 && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={handleAddContactsToSend}
                              disabled={loading}
                            >
                              Add to SEND ({selectedCrmContacts.size})
                            </Button>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flexShrink: 0,
                          }}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handleCheckKasprCredits}
                            disabled={loading}
                          >
                            Credits
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handleAiVerifyProspects}
                            disabled={loading || selectedCrmContacts.size === 0 || aiVerifyLoading}
                            startIcon={aiVerifyLoading ? <CircularProgress size={14} thickness={5} /> : null}
                          >
                            {aiVerifyLoading ? 'Verifying…' : 'AI verify prospects'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => {
                              setLinkedinImportText('');
                              setLinkedinImportResult(null);
                              setLinkedinImportOpen(true);
                            }}
                          >
                            LinkedIn
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handleExportCrmContactsCsv}
                            disabled={loading || selectedCrmContacts.size === 0 || crmContactsCsvExporting}
                          >
                            {crmContactsCsvExporting ? 'Exporting…' : 'CSV'}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              closeInlineCrmContactEdit();
                              setCrmContactFormData({
                                company_id: '',
                                first_name: '',
                                last_name: '',
                                title: '',
                                linkedin: '',
                                email: '',
                                phone: '',
                                notes: '',
                                prospect_score: -1,
                                prospect_reason: '',
                                status: 'none'
                              });
                              setCrmContactDialogOpen(true);
                            }}
                          >
                            Add Contact
                          </Button>
                        </Box>
                      </Box>
                    </Box>

                    {/* Contacts Table */}
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <TableContainer
                          component={Paper}
                          sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}
                        >
                          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" sx={{ width: 72, boxSizing: 'border-box' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                      indeterminate={selectedCrmContacts.size > 0 && selectedCrmContacts.size < crmContacts.length}
                                      checked={crmContacts.length > 0 && selectedCrmContacts.size === crmContacts.length}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedCrmContacts(new Set(crmContacts.map(c => c.id)));
                                        } else {
                                          setSelectedCrmContacts(new Set());
                                        }
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={(e) => setCrmContactMenuAnchor(e.currentTarget)}
                                      sx={{ ml: 0.5 }}
                                    >
                                      <SettingsIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  <Menu
                                    anchorEl={crmContactMenuAnchor}
                                    open={Boolean(crmContactMenuAnchor)}
                                    onClose={() => setCrmContactMenuAnchor(null)}
                                  >
                                    <MenuItem onClick={() => {
                                      setSelectedCrmContacts(new Set(crmContacts.map(c => c.id)));
                                      setCrmContactMenuAnchor(null);
                                    }}>
                                      Select All
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                      const everySecond = new Set();
                                      crmContacts.forEach((contact, idx) => {
                                        if (idx % 2 === 0) {
                                          everySecond.add(contact.id);
                                        }
                                      });
                                      setSelectedCrmContacts(everySecond);
                                      setCrmContactMenuAnchor(null);
                                    }}>
                                      Select Every Second Row
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                      setSelectedCrmContacts(new Set());
                                      setCrmContactMenuAnchor(null);
                                    }}>
                                      Clear Selection
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                                <TableCell sx={{ width: '10%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'first_name'}
                                    direction={crmContactsSortBy === 'first_name' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'first_name') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('first_name');
                                        setCrmContactsSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Name
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '12%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'email'}
                                    direction={crmContactsSortBy === 'email' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'email') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('email');
                                        setCrmContactsSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Email
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '11%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'company_id'}
                                    direction={crmContactsSortBy === 'company_id' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'company_id') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('company_id');
                                        setCrmContactsSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Company
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '18%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'title'}
                                    direction={crmContactsSortBy === 'title' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'title') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('title');
                                        setCrmContactsSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Title
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '9%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'phone'}
                                    direction={crmContactsSortBy === 'phone' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'phone') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('phone');
                                        setCrmContactsSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Phone
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '14%', boxSizing: 'border-box' }}>Notes</TableCell>
                                <TableCell sx={{ width: '5%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'prospect_score'}
                                    direction={crmContactsSortBy === 'prospect_score' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'prospect_score') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('prospect_score');
                                        setCrmContactsSortOrder('desc');
                                      }
                                    }}
                                  >
                                    Score
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '12%', boxSizing: 'border-box' }}>Reason</TableCell>
                                <TableCell sx={{ width: '8%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'status'}
                                    direction={crmContactsSortBy === 'status' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'status') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('status');
                                        setCrmContactsSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Status
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '11%', boxSizing: 'border-box' }}>
                                  <TableSortLabel
                                    active={crmContactsSortBy === 'modified'}
                                    direction={crmContactsSortBy === 'modified' ? crmContactsSortOrder : 'asc'}
                                    onClick={() => {
                                      if (crmContactsSortBy === 'modified') {
                                        setCrmContactsSortOrder(crmContactsSortOrder === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setCrmContactsSortBy('modified');
                                        setCrmContactsSortOrder('asc');
                                      }
                                    }}
                                  >
                                    Modified
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: 132, boxSizing: 'border-box' }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {crmContacts.map((contact) => {
                                const company = crmCompaniesList.find(c => c.id === contact.company_id);
                                const isInlineEditing = inlineEditingCrmContactId === contact.id;
                                return (
                                  <React.Fragment key={contact.id}>
                                  <TableRow sx={{ backgroundColor: isInlineEditing ? '#f5f5ff' : '#fff' }}>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={selectedCrmContacts.has(contact.id)}
                                        onChange={(e) => {
                                          const newSet = new Set(selectedCrmContacts);
                                          if (e.target.checked) {
                                            newSet.add(contact.id);
                                          } else {
                                            newSet.delete(contact.id);
                                          }
                                          setSelectedCrmContacts(newSet);
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        verticalAlign: 'top',
                                        overflowWrap: 'anywhere'
                                      }}
                                    >
                                      {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || '-'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        verticalAlign: 'top',
                                        overflowWrap: 'anywhere'
                                      }}
                                    >
                                      {contact.email || '-'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        verticalAlign: 'top',
                                        overflowWrap: 'anywhere'
                                      }}
                                    >
                                      {company?.name || '-'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        verticalAlign: 'top',
                                        overflowWrap: 'anywhere'
                                      }}
                                    >
                                      {contact.title || '-'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        verticalAlign: 'top',
                                        overflowWrap: 'anywhere'
                                      }}
                                    >
                                      {contact.phone || '-'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        verticalAlign: 'top',
                                        overflow: 'hidden',
                                        maxWidth: 0
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                                        {notesUpdatingContactId === contact.id ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <>
                                            <Typography
                                              component="span"
                                              sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere'
                                              }}
                                            >
                                              {contact.notes || '-'}
                                            </Typography>
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                const initialNotes = contact.notes || '';
                                                setNotesQuickEditContactId(contact.id);
                                                setNotesQuickEditValue(initialNotes);
                                                setNotesQuickEditOriginalValue(initialNotes);
                                                setNotesQuickEditAnchor(e.currentTarget);
                                              }}
                                              title="Quick edit notes"
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                      <MuiTooltip
                                        title={crmProspectReasonTooltipText(contact)}
                                        placement="top"
                                        enterDelay={280}
                                        leaveDelay={100}
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: 'background.paper',
                                              color: 'text.primary',
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              boxShadow: 3,
                                              p: 1.5,
                                              maxWidth: 420,
                                              fontSize: '0.8125rem',
                                              whiteSpace: 'pre-wrap',
                                            },
                                          },
                                        }}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 36,
                                            px: 1,
                                            py: 0.35,
                                            borderRadius: '999px',
                                            fontSize: '0.8125rem',
                                            fontWeight: 700,
                                            cursor: 'default',
                                            ...crmProspectScoreBubbleSx(contact.prospect_score),
                                          }}
                                        >
                                          {contact.prospect_score != null ? contact.prospect_score : -1}
                                        </Box>
                                      </MuiTooltip>
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        verticalAlign: 'top',
                                        maxWidth: 0,
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          display: '-webkit-box',
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          color: (contact.prospect_reason || '').trim() ? 'text.primary' : 'text.secondary',
                                        }}
                                      >
                                        {(contact.prospect_reason || '').trim() || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                      <Chip
                                        label={statusUpdatingContactId === contact.id ? '...' : contact.status}
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setStatusMenuAnchor(e.currentTarget);
                                          setStatusMenuContactId(contact.id);
                                        }}
                                        sx={{
                                          cursor: 'pointer',
                                          maxWidth: '100%',
                                          '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center' },
                                          backgroundColor:
                                            contact.status === 'email' ? '#ff9800' :
                                              contact.status === 'email_2' ? '#ffb74d' :
                                                contact.status === 'email_3' ? '#ffcc80' :
                                                  contact.status === 'phone' ? '#ffeb3b' :
                                                    contact.status === 'reply' ? '#2196f3' :
                                                      contact.status === 'won' ? '#4caf50' :
                                                        contact.status === 'lost' ? '#e91e63' :
                                                          contact.status === 'wrong' ? '#f44336' :
                                                            contact.status === 'open' ? '#F3E5AB' :
                                                              contact.status === 'click' ? '#8bc34a' : // New color for 'click'
                                                                contact.status === 'followup' ? '#9c27b0' :
                                                                  contact.status === 'demo' ? '#00bcd4' :
                                                                    contact.status === 'priority' ? '#e65100' :
                                                                      contact.status === 'unsubscribe' ? '#6366f1' :
                                                                        '#e0e0e0',
                                          color:
                                            contact.status === 'phone' ? '#000' :
                                              contact.status === 'open' ? '#000' :
                                                contact.status === 'click' ? '#000' : // New color for 'click'
                                                  contact.status === 'email' || contact.status === 'email_2' || contact.status === 'email_3' || contact.status === 'won' || contact.status === 'lost' || contact.status === 'reply' || contact.status === 'wrong' || contact.status === 'followup' || contact.status === 'demo' || contact.status === 'priority' || contact.status === 'unsubscribe' ? '#fff' :
                                                    '#000'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'normal' }}>
                                      {formatDateWithTimezone(contact.modified)}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, maxWidth: '100%' }}>
                                      {contact.email && contact.email.includes('@') && (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const domain = contact.email.split('@')[1];
                                            if (domain) {
                                              window.open(`https://${domain}`, '_blank', 'noopener,noreferrer');
                                            }
                                          }}
                                          title="Open website"
                                        >
                                          <PublicIcon />
                                        </IconButton>
                                      )}
                                      {contact.linkedin && (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            window.open(contact.linkedin, '_blank', 'noopener,noreferrer');
                                          }}
                                          title="View LinkedIn Profile"
                                        >
                                          <VisibilityIcon />
                                        </IconButton>
                                      )}
                                      <IconButton
                                        size="small"
                                        disabled={fetchingPhoneContactIds.has(contact.id)}
                                        onClick={async () => {
                                          // When multiple contacts are selected, fetch for all selected; otherwise fetch for clicked contact only
                                          const idsToFetch = selectedCrmContacts.size > 0
                                            ? Array.from(selectedCrmContacts)
                                            : [contact.id];
                                          const contactsToFetch = crmContacts.filter(c => idsToFetch.includes(c.id) && c.linkedin);
                                          const withoutLinkedin = crmContacts.filter(c => idsToFetch.includes(c.id) && !c.linkedin);
                                          if (withoutLinkedin.length > 0) {
                                            setError(`${withoutLinkedin.length} selected contact(s) do not have a LinkedIn URL. Phone numbers cannot be fetched for them.`);
                                            if (contactsToFetch.length === 0) return;
                                          }
                                          const ids = contactsToFetch.map(c => c.id);
                                          setFetchingPhoneContactIds(new Set(ids));
                                          const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
                                          let successCount = 0;
                                          let notFoundCount = 0;
                                          let stillPendingCount = 0;
                                          let postFailedCount = 0;
                                          for (const c of contactsToFetch) {
                                            const result = await apiCall('post', `/api/sales/crm/contacts/${c.id}/fetch-phone`);
                                            if (!result.success || !result.data) {
                                              postFailedCount += 1;
                                              continue;
                                            }
                                            const payload = result.data;
                                            const snap = payload.contact || payload;
                                            const baselineModified = snap.modified;
                                            const pending = payload.phone_enrichment_pending;
                                            let latest = snap;
                                            if (pending) {
                                              const deadline = Date.now() + 200000;
                                              while (Date.now() < deadline) {
                                                await sleep(3000);
                                                const poll = await apiCall('get', `/api/sales/crm/contacts/${c.id}`);
                                                if (poll.success && poll.data && poll.data.modified !== baselineModified) {
                                                  latest = poll.data;
                                                  break;
                                                }
                                              }
                                            }
                                            setCrmContacts((prev) => prev.map((c2) =>
                                              c2.id === c.id ? { ...c2, phone: latest.phone, modified: latest.modified } : c2
                                            ));
                                            if (latest.modified !== baselineModified) {
                                              if (latest.phone && latest.phone !== 'find one') {
                                                successCount += 1;
                                              } else {
                                                notFoundCount += 1;
                                              }
                                            } else if (pending) {
                                              stillPendingCount += 1;
                                            } else if (latest.phone && latest.phone !== 'find one') {
                                              successCount += 1;
                                            } else {
                                              notFoundCount += 1;
                                            }
                                          }
                                          setFetchingPhoneContactIds(new Set());
                                          const msgs = [];
                                          if (successCount > 0) msgs.push(`Phone numbers fetched for ${successCount} contact(s)`);
                                          if (notFoundCount > 0) msgs.push(`${notFoundCount} not found via FullEnrich`);
                                          if (stillPendingCount > 0) msgs.push(`${stillPendingCount} still processing — refresh shortly if the number is missing`);
                                          if (msgs.length > 0) setSuccess(msgs.join('. '));
                                          if (postFailedCount > 0) {
                                            setError(postFailedCount >= contactsToFetch.length
                                              ? 'Failed to fetch phone numbers'
                                              : `${postFailedCount} phone fetch request(s) failed`);
                                          } else if (msgs.length === 0 && contactsToFetch.length > 0) {
                                            setError('Failed to fetch phone numbers');
                                          }
                                        }}
                                        title="Fetch phone number from FullEnrich"
                                      >
                                        {fetchingPhoneContactIds.has(contact.id) ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <PhoneIcon />
                                        )}
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleInlineCrmContactEdit(contact)}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={async () => {
                                          if (window.confirm(`Delete contact "${contact.email || 'this contact'}"?`)) {
                                            setLoading(true);
                                            const result = await apiCall('delete', `/api/sales/crm/contacts/${contact.id}`);
                                            setLoading(false);
                                            if (result.success) {
                                              setSuccess('Contact deleted');
                                              // Reload contacts
                                              let url = `/api/sales/crm/contacts?page=${crmContactsPage + 1}&per_page=${crmContactsRowsPerPage}`;
                                              if (crmCompanyFilter) url += `&company_id=${crmCompanyFilter}`;
                                              crmContactsStatusFilter.forEach((s) => { url += `&status_filter=${encodeURIComponent(s)}`; });
                                              if (crmSearchFilter) url += `&search=${encodeCrmSearchParam(crmSearchFilter)}`;
                                              if (crmPhoneFilter) url += `&has_phone=true`;
                                              if (crmNotesFilter) url += `&has_notes=true`;
                                              url = appendCrmContactsProspectScoreParams(url, crmContactsScoreFilter);
                                              url += `&sort_by=${crmContactsSortBy}&sort_order=${crmContactsSortOrder}`;
                                              const reloadResult = await apiCall('get', url);
                                              if (reloadResult.success && reloadResult.data) {
                                                setCrmContacts(reloadResult.data.contacts || []);
                                                setCrmContactsTotal(reloadResult.data.total || 0);
                                              }
                                            } else {
                                              setError(result.error || 'Failed to delete contact');
                                            }
                                          }
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                  {isInlineEditing && (
                                    <TableRow>
                                      <TableCell
                                        colSpan={12}
                                        sx={{
                                          p: 0,
                                          borderBottom: '2px solid',
                                          borderColor: 'primary.main',
                                          backgroundColor: '#e8f5e9',
                                        }}
                                      >
                                        <Box sx={{
                                          p: 3,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 2.5,
                                          '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                          },
                                        }}>
                                          <Typography variant="subtitle1" fontWeight={600}>Edit Contact</Typography>
                                          <Grid container spacing={2.5}>
                                            <Grid item xs={12} md={8}>
                                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                <FormControl fullWidth required sx={{ flex: 1 }}>
                                                  <InputLabel>Company *</InputLabel>
                                                  <Select
                                                    value={crmContactFormData.company_id}
                                                    label="Company *"
                                                    onChange={(e) => setCrmContactFormData({ ...crmContactFormData, company_id: e.target.value })}
                                                    required
                                                  >
                                                    {crmCompaniesList.map((c) => (
                                                      <MenuItem key={c.id} value={c.id}>
                                                        {c.name}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                </FormControl>
                                                <IconButton
                                                  aria-label="Edit company"
                                                  disabled={!crmContactFormData.company_id}
                                                  onClick={() => {
                                                    const c = crmCompaniesList.find((co) => co.id === crmContactFormData.company_id);
                                                    openCrmCompanyEdit(c);
                                                  }}
                                                  sx={{ mt: 1 }}
                                                >
                                                  <EditIcon />
                                                </IconButton>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <FormControl fullWidth>
                                                <InputLabel>Status</InputLabel>
                                                <Select
                                                  value={crmContactFormData.status}
                                                  label="Status"
                                                  onChange={(e) => setCrmContactFormData({ ...crmContactFormData, status: e.target.value })}
                                                >
                                                  <MenuItem value="none">None</MenuItem>
                                                  <MenuItem value="email">Email</MenuItem>
                                                  <MenuItem value="email_2">Email 2</MenuItem>
                                                  <MenuItem value="email_3">Email 3</MenuItem>
                                                  <MenuItem value="phone">Phone</MenuItem>
                                                  <MenuItem value="reply">Reply</MenuItem>
                                                  <MenuItem value="won">Won</MenuItem>
                                                  <MenuItem value="lost">Lost</MenuItem>
                                                  <MenuItem value="wrong">Wrong</MenuItem>
                                                  <MenuItem value="open">Open</MenuItem>
                                                  <MenuItem value="click">Click</MenuItem>
                                                  <MenuItem value="followup">Followup</MenuItem>
                                                  <MenuItem value="demo">Demo</MenuItem>
                                                  <MenuItem value="priority">Priority</MenuItem>
                                                  <MenuItem value="unsubscribe">Unsubscribe</MenuItem>
                                                </Select>
                                              </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <TextField
                                                label="First Name"
                                                fullWidth
                                                value={crmContactFormData.first_name}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, first_name: e.target.value })}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <TextField
                                                label="Last Name"
                                                fullWidth
                                                value={crmContactFormData.last_name}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, last_name: e.target.value })}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <TextField
                                                label="Title"
                                                fullWidth
                                                value={crmContactFormData.title}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, title: e.target.value })}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <TextField
                                                label="Email"
                                                fullWidth
                                                type="email"
                                                value={crmContactFormData.email}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, email: e.target.value })}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <TextField
                                                label="Phone"
                                                fullWidth
                                                value={crmContactFormData.phone}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, phone: e.target.value })}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <TextField
                                                label="LinkedIn"
                                                fullWidth
                                                value={crmContactFormData.linkedin}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, linkedin: e.target.value })}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                              <TextField
                                                label="Score"
                                                type="number"
                                                fullWidth
                                                value={crmContactFormData.prospect_score}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  setCrmContactFormData({
                                                    ...crmContactFormData,
                                                    prospect_score: val === '' ? -1 : parseInt(val, 10),
                                                  });
                                                }}
                                                inputProps={{ min: -1, max: 10, step: 1 }}
                                                helperText="-1 to 10"
                                                sx={{ maxWidth: 160 }}
                                              />
                                            </Grid>
                                            <Grid item xs={12}>
                                              <TextField
                                                label="Reasoning"
                                                fullWidth
                                                multiline
                                                minRows={6}
                                                value={crmContactFormData.prospect_reason}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, prospect_reason: e.target.value })}
                                              />
                                            </Grid>
                                            <Grid item xs={12}>
                                              <TextField
                                                label="Notes"
                                                fullWidth
                                                multiline
                                                minRows={6}
                                                value={crmContactFormData.notes}
                                                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, notes: e.target.value })}
                                              />
                                            </Grid>
                                          </Grid>
                                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                            <Button
                                              onClick={closeInlineCrmContactEdit}
                                              disabled={savingCrmContact}
                                            >
                                              Cancel
                                            </Button>
                                            <Button
                                              variant="contained"
                                              disabled={savingCrmContact}
                                              startIcon={savingCrmContact ? <CircularProgress size={20} color="inherit" /> : null}
                                              onClick={() => handleSaveCrmContact({
                                                isCreate: false,
                                                onSuccess: closeInlineCrmContactEdit,
                                              })}
                                            >
                                              {savingCrmContact ? 'Saving...' : 'Save'}
                                            </Button>
                                          </Box>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Menu
                          anchorEl={statusMenuAnchor}
                          open={Boolean(statusMenuAnchor)}
                          onClose={() => {
                            setStatusMenuAnchor(null);
                            setStatusMenuContactId(null);
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          MenuListProps={{ sx: { py: 0 } }}
                        >
                          {(() => {
                            const contact = crmContacts.find(c => c.id === statusMenuContactId);
                            return ['none', 'email', 'email_2', 'email_3', 'phone', 'reply', 'won', 'lost', 'wrong', 'open', 'click', 'followup', 'demo', 'priority', 'unsubscribe'].map((statusOption) => (
                              <MenuItem
                                key={statusOption}
                                selected={contact?.status === statusOption}
                                onClick={() => handleInlineStatusChange(statusMenuContactId, statusOption)}
                              >
                                {statusOption.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase())}
                              </MenuItem>
                            ));
                          })()}
                        </Menu>
                        <Popover
                          open={Boolean(notesQuickEditAnchor)}
                          anchorEl={notesQuickEditAnchor}
                          onClose={() => {
                            if (notesQuickEditContactId) {
                              handleQuickEditNotesSave(notesQuickEditContactId);
                            } else {
                              setNotesQuickEditAnchor(null);
                              setNotesQuickEditContactId(null);
                            }
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                          <Box sx={{ p: 2, minWidth: 480, minHeight: 280 }}>
                            <TextField
                              label="Notes"
                              fullWidth
                              multiline
                              rows={10}
                              value={notesQuickEditValue}
                              onChange={(e) => setNotesQuickEditValue(e.target.value)}
                              disabled={notesUpdatingContactId === notesQuickEditContactId}
                              sx={{ mb: 2 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleQuickEditNotesSave(notesQuickEditContactId)}
                              disabled={notesUpdatingContactId === notesQuickEditContactId}
                            >
                              {notesUpdatingContactId === notesQuickEditContactId ? 'Saving...' : 'Save'}
                            </Button>
                          </Box>
                        </Popover>
                        <TablePagination
                          component="div"
                          count={crmContactsTotal}
                          page={crmContactsPage}
                          onPageChange={(e, newPage) => setCrmContactsPage(newPage)}
                          rowsPerPage={crmContactsRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setCrmContactsRowsPerPage(parseInt(e.target.value, 10));
                            setCrmContactsPage(0);
                          }}
                          rowsPerPageOptions={[25, 50, 100]}
                        />
                      </>
                    )}
                  </Box>
                )}

                {/* STATISTICS VIEW */}
                {crmTab === 'statistics' && (
                  <Box>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6">Contacts Statistics</Typography>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={async () => {
                          setLoadingCrmStatistics(true);
                          const result = await apiCall('get', '/api/sales/crm/contacts/statistics');
                          if (result.success && result.data) {
                            setCrmContactsStatistics(result.data);
                          }
                          setLoadingCrmStatistics(false);
                        }}
                        disabled={loadingCrmStatistics}
                      >
                        Refresh
                      </Button>
                    </Box>

                    {loadingCrmStatistics ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : crmContactsStatistics ? (
                      <>
                        {/* Contacts by Status Section */}
                        <Paper sx={{ p: 3, mb: 3 }}>
                          <Typography variant="h6" gutterBottom>
                            Contacts by Status
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Total Contacts: {crmContactsStatistics.total_contacts}
                          </Typography>

                          <Grid container spacing={3}>
                            {/* Pie Chart */}
                            <Grid item xs={12} md={6}>
                              <Box sx={{ height: 400 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={crmContactsStatistics.contacts_by_status}
                                      cx="50%"
                                      cy="50%"
                                      labelLine={false}
                                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                      outerRadius={120}
                                      fill="#8884d8"
                                      dataKey="count"
                                      nameKey="status"
                                    >
                                      {crmContactsStatistics.contacts_by_status.map((entry, index) => {
                                        const colors = {
                                          'none': '#e0e0e0',
                                          'email': '#ff9800',
                                          'email_2': '#ffb74d',
                                          'email_3': '#ffcc80',
                                          'phone': '#ffeb3b',
                                          'reply': '#2196f3',
                                          'won': '#4caf50',
                                          'lost': '#e91e63',
                                          'wrong': '#f44336',
                                          'open': '#F3E5AB',
                                          'followup': '#9c27b0',
                                          'demo': '#00bcd4',
                                          'priority': '#e65100',
                                          'unsubscribe': '#6366f1',
                                          'click': '#8bc34a'
                                        };
                                        return (
                                          <Cell key={`cell-${index}`} fill={colors[entry.status] || '#e0e0e0'} />
                                        );
                                      })}
                                    </Pie>
                                    <Tooltip />
                                    <Legend
                                      formatter={(value) => {
                                        // Capitalize first letter of status name
                                        return value.charAt(0).toUpperCase() + value.slice(1);
                                      }}
                                    />
                                  </PieChart>
                                </ResponsiveContainer>
                              </Box>
                            </Grid>

                            {/* Status Cards */}
                            <Grid item xs={12} md={6}>
                              <Grid container spacing={2}>
                                {/* Delivered = Email + Open (computed, not shown on chart) */}
                                {(() => {
                                  const emailCount = crmContactsStatistics.contacts_by_status.find(s => s.status === 'email')?.count ?? 0;
                                  const email2Count = crmContactsStatistics.contacts_by_status.find(s => s.status === 'email_2')?.count ?? 0;
                                  const email3Count = crmContactsStatistics.contacts_by_status.find(s => s.status === 'email_3')?.count ?? 0;
                                  const openCount = crmContactsStatistics.contacts_by_status.find(s => s.status === 'open')?.count ?? 0;
                                  const clickCount = crmContactsStatistics.contacts_by_status.find(s => s.status === 'click')?.count ?? 0;
                                  const deliveredCount = emailCount + email2Count + email3Count + openCount + clickCount;
                                  const deliveredPercentage = crmContactsStatistics.total_contacts > 0
                                    ? ((deliveredCount / crmContactsStatistics.total_contacts) * 100).toFixed(1)
                                    : '0.0';
                                  return (
                                    <Grid item xs={6} sm={4} key="delivered">
                                      <Paper
                                        sx={{
                                          p: 2,
                                          textAlign: 'center',
                                          backgroundColor: '#2e7d32',
                                          color: '#fff'
                                        }}
                                      >
                                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                          {deliveredCount}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textTransform: 'capitalize', mt: 0.5 }}>
                                          Delivered
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                          {deliveredPercentage}%
                                        </Typography>
                                      </Paper>
                                    </Grid>
                                  );
                                })()}
                                {(() => {
                                  const statusesForDisplay = [...crmContactsStatistics.contacts_by_status];
                                  const noneIdx = statusesForDisplay.findIndex(s => s.status === 'none');
                                  const openIdx = statusesForDisplay.findIndex(s => s.status === 'open');
                                  if (noneIdx !== -1 && openIdx !== -1) {
                                    [statusesForDisplay[noneIdx], statusesForDisplay[openIdx]] = [statusesForDisplay[openIdx], statusesForDisplay[noneIdx]];
                                  }
                                  return statusesForDisplay;
                                })().map((item) => {
                                  const statusColors = {
                                    'none': { bg: '#e0e0e0', text: '#000' },
                                    'email': { bg: '#ff9800', text: '#fff' },
                                    'email_2': { bg: '#ffb74d', text: '#fff' },
                                    'email_3': { bg: '#ffcc80', text: '#fff' },
                                    'phone': { bg: '#ffeb3b', text: '#000' },
                                    'reply': { bg: '#2196f3', text: '#fff' },
                                    'won': { bg: '#4caf50', text: '#fff' },
                                    'lost': { bg: '#e91e63', text: '#fff' },
                                    'wrong': { bg: '#f44336', text: '#fff' },
                                    'open': { bg: '#F3E5AB', text: '#000' },
                                    'followup': { bg: '#9c27b0', text: '#fff' },
                                    'demo': { bg: '#00bcd4', text: '#fff' },
                                    'priority': { bg: '#e65100', text: '#fff' },
                                    'unsubscribe': { bg: '#6366f1', text: '#fff' },
                                    'click': { bg: '#8bc34a', text: '#000' }
                                  };
                                  const colors = statusColors[item.status] || { bg: '#e0e0e0', text: '#000' };
                                  const percentage = crmContactsStatistics.total_contacts > 0
                                    ? ((item.count / crmContactsStatistics.total_contacts) * 100).toFixed(1)
                                    : '0.0';

                                  return (
                                    <Grid item xs={6} sm={4} key={item.status}>
                                      <Paper
                                        sx={{
                                          p: 2,
                                          textAlign: 'center',
                                          backgroundColor: colors.bg,
                                          color: colors.text
                                        }}
                                      >
                                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                          {item.count}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textTransform: 'capitalize', mt: 0.5 }}>
                                          {item.status}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                          {percentage}%
                                        </Typography>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Paper>
                      </>
                    ) : (
                      <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          No statistics available
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        ) : selectedTab !== 'settings' && selectedTab !== 'statistics' && (

          <Box sx={{ flexGrow: 1, pb: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      {renderTableHeaders()}
                    </TableHead>
                    <TableBody>
                      {data.map((item) => renderTableRow(item))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100, 200]}
                  component="div"
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Box>
        )}
      </Box>

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setDialogError(''); setCompanyRewardProviders([]); }} maxWidth={selectedTab === 'companies' ? "md" : "sm"} fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit' : 'Add'} {selectedTab === 'companies' ? 'manufacturer' : selectedTab.slice(0, -1)}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof dialogError === 'string' ? dialogError : JSON.stringify(dialogError)}
            </Alert>
          )}
          {renderFormFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setDialogError(''); setCompanyRewardProviders([]); }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBulkDeleteDialog}
        onClose={() => {
          setOpenBulkDeleteDialog(false);
          setBulkDeleteConfirmed(false);
          setDialogError('');
        }}
      >
        <DialogTitle>
          Confirm Bulk Deletion
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof dialogError === 'string' ? dialogError : JSON.stringify(dialogError)}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to delete <strong>{selectedTab === 'users' ? selectedUserIds.length : selectedDealerIds.length}</strong> {selectedTab === 'users' ? 'user(s)' : 'dealer(s)'}. This action cannot be undone.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Checkbox
              checked={bulkDeleteConfirmed}
              onChange={(e) => setBulkDeleteConfirmed(e.target.checked)}
            />
            <Typography variant="body2">
              I confirm that I want to delete these {selectedTab === 'users' ? selectedUserIds.length : selectedDealerIds.length} {selectedTab === 'users' ? 'user(s)' : 'dealer(s)'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenBulkDeleteDialog(false);
            setBulkDeleteConfirmed(false);
            setDialogError('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={selectedTab === 'users' ? handleBulkDeleteConfirm : handleBulkDeleteDealersConfirm}
            variant="contained"
            color="error"
            disabled={!bulkDeleteConfirmed || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDealerDialog}
        onClose={() => {
          setOpenDeleteDealerDialog(false);
          setDeletingDealerId(null);
          setDeleteDealerConfirmed(false);
          setDialogError('');
        }}
      >
        <DialogTitle>
          Confirm Dealer Deletion
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof dialogError === 'string' ? dialogError : JSON.stringify(dialogError)}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to delete this dealer. This will also delete all associated goals and performance data. This action cannot be undone.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Checkbox
              checked={deleteDealerConfirmed}
              onChange={(e) => setDeleteDealerConfirmed(e.target.checked)}
            />
            <Typography variant="body2">
              I confirm that I want to delete this dealer and all associated data
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDeleteDealerDialog(false);
            setDeletingDealerId(null);
            setDeleteDealerConfirmed(false);
            setDialogError('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleDealerDeleteConfirm}
            variant="contained"
            color="error"
            disabled={!deleteDealerConfirmed || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteUserDialog}
        onClose={() => {
          setOpenDeleteUserDialog(false);
          setDeletingUserId(null);
          setDeleteUserConfirmed(false);
          setDialogError('');
        }}
      >
        <DialogTitle>
          Confirm User Deletion
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof dialogError === 'string' ? dialogError : JSON.stringify(dialogError)}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to delete this user. This action cannot be undone.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Checkbox
              checked={deleteUserConfirmed}
              onChange={(e) => setDeleteUserConfirmed(e.target.checked)}
            />
            <Typography variant="body2">
              I confirm that I want to delete this user
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDeleteUserDialog(false);
            setDeletingUserId(null);
            setDeleteUserConfirmed(false);
            setDialogError('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleUserDeleteConfirm}
            variant="contained"
            color="error"
            disabled={!deleteUserConfirmed || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Sales Communication Dialog */}
      <Dialog
        open={viewCommDialogOpen}
        onClose={() => {
          setViewCommDialogOpen(false);
          setSelectedComm(null);
          // Reset sorting to default
          setRecipientsSortBy('modified');
          setRecipientsSortOrder('desc');
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Sales Communication Details
        </DialogTitle>
        <DialogContent>
          {selectedComm && (
            <Box>
              {/* Communication Info */}
              <Typography variant="h6" gutterBottom>Communication Info</Typography>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2"><strong>Title:</strong> {selectedComm.title}</Typography>
                {selectedComm.subtitle && (
                  <Typography variant="body2" sx={{ mt: 1 }}><strong>Subtitle:</strong> {selectedComm.subtitle}</Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Sent:</strong> {formatDateWithTimezone(selectedComm.created)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Recipients:</strong> {selectedComm.recipient_count}
                </Typography>
              </Box>

              {/* Content Preview */}
              <Typography variant="h6" gutterBottom>Content Preview</Typography>
              <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Content:</Typography>
                <Box
                  sx={{ mb: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}
                  dangerouslySetInnerHTML={{ __html: processHtmlForDisplay(selectedComm.content) }}
                />

                {selectedComm.extra_content && (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Extra Content:</Typography>
                    <Box
                      sx={{ mb: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}
                      dangerouslySetInnerHTML={{ __html: processHtmlForDisplay(selectedComm.extra_content) }}
                    />
                  </>
                )}

                <Typography variant="body2" color="text.secondary" gutterBottom>CTA Button:</Typography>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="body2"><strong>Title:</strong> {selectedComm.custom_cta_title}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>URL:</strong> <a href={selectedComm.custom_cta_url} target="_blank" rel="noopener noreferrer">{selectedComm.custom_cta_url}</a>
                  </Typography>
                </Box>
              </Box>

              {/* Recipients List */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Recipients</Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleUpdateStatusFromCommunication(selectedComm.id)}
                  disabled={loading}
                >
                  Update Status
                </Button>
              </Box>
              <TableContainer component={Paper} sx={{ maxHeight: 400, bgcolor: 'white' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={recipientsSortBy === 'email'}
                          direction={recipientsSortBy === 'email' ? recipientsSortOrder : 'asc'}
                          onClick={() => handleRecipientsSort('email')}
                        >
                          Email
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={recipientsSortBy === 'name'}
                          direction={recipientsSortBy === 'name' ? recipientsSortOrder : 'asc'}
                          onClick={() => handleRecipientsSort('name')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={recipientsSortBy === 'company_name'}
                          direction={recipientsSortBy === 'company_name' ? recipientsSortOrder : 'asc'}
                          onClick={() => handleRecipientsSort('company_name')}
                        >
                          Company
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={recipientsSortBy === 'title'}
                          direction={recipientsSortBy === 'title' ? recipientsSortOrder : 'asc'}
                          onClick={() => handleRecipientsSort('title')}
                        >
                          Title
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={recipientsSortBy === 'status'}
                          direction={recipientsSortBy === 'status' ? recipientsSortOrder : 'asc'}
                          onClick={() => handleRecipientsSort('status')}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={recipientsSortBy === 'modified'}
                          direction={recipientsSortBy === 'modified' ? recipientsSortOrder : 'asc'}
                          onClick={() => handleRecipientsSort('modified')}
                        >
                          Modified
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedComm.recipients && (() => {
                      // Sort recipients
                      const sortedRecipients = [...selectedComm.recipients].sort((a, b) => {
                        let aVal, bVal;

                        switch (recipientsSortBy) {
                          case 'email':
                            aVal = (a.email || '').toLowerCase();
                            bVal = (b.email || '').toLowerCase();
                            break;
                          case 'name':
                            aVal = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                            bVal = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                            break;
                          case 'company_name':
                            aVal = (a.company_name || '').toLowerCase();
                            bVal = (b.company_name || '').toLowerCase();
                            break;
                          case 'title':
                            aVal = (a.title || '').toLowerCase();
                            bVal = (b.title || '').toLowerCase();
                            break;
                          case 'status':
                            aVal = (a.status || '').toLowerCase();
                            bVal = (b.status || '').toLowerCase();
                            break;
                          case 'modified':
                            aVal = a.modified ? new Date(a.modified).getTime() : 0;
                            bVal = b.modified ? new Date(b.modified).getTime() : 0;
                            break;
                          default:
                            return 0;
                        }

                        if (aVal < bVal) return recipientsSortOrder === 'asc' ? -1 : 1;
                        if (aVal > bVal) return recipientsSortOrder === 'asc' ? 1 : -1;
                        return 0;
                      });

                      return sortedRecipients.map((recipient, idx) => (
                        <TableRow key={idx} sx={{ bgcolor: 'white' }}>
                          <TableCell>{recipient.email}</TableCell>
                          <TableCell>{recipient.first_name} {recipient.last_name}</TableCell>
                          <TableCell>{recipient.company_name || '-'}</TableCell>
                          <TableCell>{recipient.title || '-'}</TableCell>
                          <TableCell>
                            {recipient.status === 'CLICK' || recipient.status === 'CLICKED' ? (
                              <Chip label="CLICKED" color="success" size="small" />
                            ) : recipient.status === 'UNSUBSCRIBE' ? (
                              <Chip label="UNSUBSCRIBED" color="warning" size="small" />
                            ) : recipient.status === 'OPEN' || recipient.status === 'OPENED' ? (
                              <Chip label="OPENED" color="info" size="small" />
                            ) : recipient.status === 'DELIVERED' ? (
                              <Chip label="DELIVERED" color="primary" size="small" />
                            ) : recipient.status === 'BOUNCE' || recipient.status === 'BOUNCED' ? (
                              <Chip label="BOUNCED" color="error" size="small" />
                            ) : recipient.status === 'DROPPED' || recipient.status === 'BLOCKED' ? (
                              <Chip label={recipient.status} color="error" size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {recipient.modified ? formatDateWithTimezone(recipient.modified) : '-'}
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Email Statistics */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Email Statistics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                      <Typography variant="h4">{selectedComm.delivered_count}</Typography>
                      <Typography variant="body2" color="text.secondary">Delivered</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                      <Typography variant="h4">{selectedComm.unique_opened_count}</Typography>
                      <Typography variant="body2" color="text.secondary">Opened</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                      <Typography variant="h4">{selectedComm.unique_clicked_count}</Typography>
                      <Typography variant="body2" color="text.secondary">Clicked</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                      <Typography variant="h4">{selectedComm.bounce_count}</Typography>
                      <Typography variant="body2" color="text.secondary">Bounced</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                      <Typography variant="h4">{selectedComm.unsubscribe_count ?? 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Unsubscribed</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewCommDialogOpen(false);
            setSelectedComm(null);
            // Reset sorting to default
            setRecipientsSortBy('modified');
            setRecipientsSortOrder('desc');
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Single Communication Dialog */}
      <Dialog
        open={deleteCommDialogOpen}
        onClose={() => {
          setDeleteCommDialogOpen(false);
          setCommToDelete(null);
        }}
      >
        <DialogTitle>Delete Sales Communication</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this sales communication?
            {commToDelete && (
              <>
                <br /><br />
                <strong>Title:</strong> {commToDelete.title}<br />
                <strong>Recipients:</strong> {commToDelete.recipient_count}<br />
                <br />
                This will also delete all related email events. This action cannot be undone.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteCommDialogOpen(false);
            setCommToDelete(null);
          }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (commToDelete) {
                setLoading(true);
                const result = await apiCall('delete', `/api/sales/communication/${commToDelete.id}`);
                setLoading(false);
                setDeleteCommDialogOpen(false);
                setCommToDelete(null);

                if (result.success) {
                  setSuccess('Communication deleted successfully');
                  // Reload history
                  const historyResult = await apiCall('get', `/api/sales/history?page=${salesPage + 1}&per_page=${salesRowsPerPage}&sort_by=${salesSortBy}&sort_order=${salesSortOrder}`);
                  if (historyResult.success) {
                    setSalesHistory(historyResult.data.communications || []);
                    setSalesTotal(historyResult.data.total || 0);
                  }
                  // Clear selection
                  setSelectedSalesComms([]);
                } else {
                  setError(result.error || 'Failed to delete communication');
                }
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Bulk Communications Dialog */}
      <Dialog
        open={deleteBulkDialogOpen}
        onClose={() => setDeleteBulkDialogOpen(false)}
      >
        <DialogTitle>Delete Selected Communications</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedSalesComms.length} selected communication{selectedSalesComms.length !== 1 ? 's' : ''}?
            <br /><br />
            This will also delete all related email events. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBulkDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              setLoading(true);
              const result = await apiCall('post', '/api/sales/communications/delete-bulk', selectedSalesComms);
              setLoading(false);
              setDeleteBulkDialogOpen(false);

              if (result.success) {
                setSuccess(`Deleted ${result.data.deleted_count} communication(s) successfully`);
                // Reload history
                const historyResult = await apiCall('get', `/api/sales/history?page=${salesPage + 1}&per_page=${salesRowsPerPage}`);
                if (historyResult.success) {
                  setSalesHistory(historyResult.data.communications || []);
                  setSalesTotal(historyResult.data.total || 0);
                }
                // Clear selection
                setSelectedSalesComms([]);
              } else {
                setError(result.error || 'Failed to delete communications');
              }
            }}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* LinkedIn bulk import (CRM contacts) */}
      <Dialog
        open={linkedinImportOpen}
        onClose={() => {
          if (linkedinImportLoading) return;
          setLinkedinImportOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import contacts from LinkedIn</DialogTitle>
        <DialogContent>
          {!linkedinImportResult ? (
            <TextField
              label="LinkedIn profile URLs"
              placeholder="One URL per line"
              multiline
              minRows={8}
              fullWidth
              value={linkedinImportText}
              onChange={(e) => setLinkedinImportText(e.target.value)}
              sx={{ mt: 1 }}
              disabled={linkedinImportLoading}
            />
          ) : (
            <Box sx={{ mt: 1 }}>
              {(linkedinImportResult.failed ?? 0) > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Kaspr / API failures
                  </Typography>
                  <Typography variant="body2">
                    Each failed line shows the reason from Kaspr (e.g. HTTP 403 &quot;workspace suspended&quot;, auth, or credits).
                    Contacts are only created when Kaspr returns a profile; use the Credits button to check your Kaspr account.
                  </Typography>
                </Alert>
              )}
              {(linkedinImportResult.created ?? 0) === 0 &&
                (linkedinImportResult.failed ?? 0) === 0 &&
                ((linkedinImportResult.skipped_duplicate_linkedin ?? 0) > 0 ||
                  (linkedinImportResult.skipped_duplicate_email ?? 0) > 0 ||
                  (linkedinImportResult.invalid ?? 0) > 0) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No new rows were added: every URL was skipped (already in CRM, duplicate email, or invalid line). Adjust
                    URLs or clear duplicates and try again.
                  </Alert>
                )}
              <Typography variant="body2" gutterBottom>
                Created: {linkedinImportResult.created} · New companies: {linkedinImportResult.companies_created}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Skipped (same LinkedIn): {linkedinImportResult.skipped_duplicate_linkedin} · Skipped (email exists):{' '}
                {linkedinImportResult.skipped_duplicate_email}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Invalid lines: {linkedinImportResult.invalid} · Failed: {linkedinImportResult.failed}
              </Typography>
              {linkedinImportResult.errors && linkedinImportResult.errors.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Per-URL detail ({linkedinImportResult.errors.length})
                  </Typography>
                  {linkedinImportResult.errors.slice(0, 12).map((err, idx) => (
                    <Typography key={idx} variant="caption" display="block">
                      {err.url}: {err.detail}
                    </Typography>
                  ))}
                  {linkedinImportResult.errors.length > 12 && (
                    <Typography variant="caption" display="block">
                      …and more
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          )}
          {linkedinImportLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={32} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!linkedinImportResult ? (
            <>
              <Button onClick={() => setLinkedinImportOpen(false)} disabled={linkedinImportLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleLinkedinImport}
                disabled={linkedinImportLoading || !linkedinImportText.trim()}
              >
                Import
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setLinkedinImportOpen(false);
                  setLinkedinImportResult(null);
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const ids = linkedinImportResult.contact_ids;
                  if (ids && ids.length > 0) {
                    setCrmContactsIdFilter([...ids]);
                  }
                  setCrmCompanyFilter('');
                  setCrmSearchFilter('');
                  setCrmContactsStatusFilter([]);
                  setCrmPhoneFilter(false);
                  setCrmNotesFilter(false);
                  setCrmContactsPage(0);
                  setSelectedCrmContacts(new Set());
                  setLinkedinImportOpen(false);
                  setLinkedinImportResult(null);
                }}
                disabled={!linkedinImportResult.contact_ids || linkedinImportResult.contact_ids.length === 0}
              >
                Just Imported
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiVerifyOpen}
        onClose={() => {
          if (aiVerifyApplyLoading) return;
          setAiVerifyOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Prospect verification results</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review AI scores (0–10). Unverified contacts stored as -1 remain unchanged until you click Update.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aiVerifyResults.map((row) => (
                  <TableRow key={row.contact_id}>
                    <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.name || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{row.email || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.company_name || '—'}</TableCell>
                    <TableCell align="right">{row.prospect_score}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 280 }}>{row.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiVerifyOpen(false)} disabled={aiVerifyApplyLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAiVerifyApply}
            disabled={aiVerifyApplyLoading || aiVerifyResults.length === 0}
          >
            {aiVerifyApplyLoading ? 'Updating…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CRM Company Dialog */}
      <Dialog open={crmCompanyDialogOpen} onClose={() => setCrmCompanyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCrmCompany ? 'Edit' : 'Add'} Company
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Company Name *"
              fullWidth
              value={crmCompanyFormData.name}
              onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, name: e.target.value })}
              required
            />
            <TextField
              label="Website"
              fullWidth
              value={crmCompanyFormData.www}
              onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, www: e.target.value })}
            />
            <TextField
              label="LinkedIn"
              fullWidth
              value={crmCompanyFormData.linkedin}
              onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, linkedin: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={crmCompanyFormData.email}
              onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, email: e.target.value })}
            />
            <TextField
              label="Phone"
              fullWidth
              value={crmCompanyFormData.phone}
              onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, phone: e.target.value })}
            />
            <TextField
              label="Industry"
              fullWidth
              value={crmCompanyFormData.industry}
              onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, industry: e.target.value })}
            />
            <TextField
              label="Time Zone"
              fullWidth
              value={crmCompanyFormData.time_zone}
              onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, time_zone: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={crmCompanyFormData.status}
                label="Status"
                onChange={(e) => setCrmCompanyFormData({ ...crmCompanyFormData, status: e.target.value })}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="email_2">Email 2</MenuItem>
                <MenuItem value="email_3">Email 3</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="reply">Reply</MenuItem>
                <MenuItem value="won">Won</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
                <MenuItem value="wrong">Wrong</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="click">Click</MenuItem>
                <MenuItem value="followup">Followup</MenuItem>
                <MenuItem value="demo">Demo</MenuItem>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="unsubscribe">Unsubscribe</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrmCompanyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!crmCompanyFormData.name.trim()) {
                setError('Company name is required');
                return;
              }
              setLoading(true);
              const method = editingCrmCompany ? 'put' : 'post';
              const url = editingCrmCompany
                ? `/api/sales/crm/companies/${editingCrmCompany.id}`
                : '/api/sales/crm/companies';
              const result = await apiCall(method, url, crmCompanyFormData);
              setLoading(false);
              if (result.success) {
                setSuccess(editingCrmCompany ? 'Company updated' : 'Company created');
                setCrmCompanyDialogOpen(false);
                // Reload companies
                let reloadUrl = `/api/sales/crm/companies?page=${crmCompaniesPage + 1}&per_page=${crmCompaniesRowsPerPage}`;
                if (crmStatusFilter) reloadUrl += `&status_filter=${crmStatusFilter}`;
                if (crmSearchFilter) reloadUrl += `&search=${encodeCrmSearchParam(crmSearchFilter)}`;
                if (crmCompaniesPhoneFilter) reloadUrl += `&has_phone=true`;
                reloadUrl += `&sort_by=${crmCompaniesSortBy}&sort_order=${crmCompaniesSortOrder}`;
                const reloadResult = await apiCall('get', reloadUrl);
                if (reloadResult.success && reloadResult.data) {
                  setCrmCompanies(reloadResult.data.companies || []);
                  setCrmCompaniesTotal(reloadResult.data.total || 0);
                }
                // Reload companies list for dropdown
                const listResult = await apiCall('get', '/api/sales/crm/companies?page=1&per_page=1000');
                if (listResult.success && listResult.data) {
                  setCrmCompaniesList(listResult.data.companies || []);
                }
              } else {
                setError(result.error || 'Failed to save company');
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kaspr Credits Dialog */}
      <Dialog open={kasprCreditsDialogOpen} onClose={() => setKasprCreditsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kaspr Credits Breakdown</DialogTitle>
        <DialogContent dividers>
          {kasprCredits ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Credit Type</strong></TableCell>
                    <TableCell align="right"><strong>Remaining</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(kasprCredits)
                    .filter(([key]) => key.toLowerCase().startsWith('remaining-') && key.toLowerCase().endsWith('-credits') && key.toLowerCase() !== 'remaining-credits')
                    .map(([key, value]) => {
                      const label = key
                        .replace(/^remaining-/i, '')
                        .replace(/-credits$/i, '')
                        .replace(/-/g, ' ');
                      return (
                        <TableRow key={key}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{label || 'General'}</TableCell>
                          <TableCell align="right">{value}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No credits data available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKasprCreditsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* CRM Contact Dialog */}
      <Dialog
        open={crmContactDialogOpen}
        onClose={(event, reason) => {
          if (crmCompanyDialogOpen) return;
          setCrmContactDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Add Contact
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl fullWidth required sx={{ flex: 1 }}>
                <InputLabel>Company *</InputLabel>
                <Select
                  value={crmContactFormData.company_id}
                  label="Company *"
                  onChange={(e) => setCrmContactFormData({ ...crmContactFormData, company_id: e.target.value })}
                  required
                >
                  {crmCompaniesList.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                aria-label="Edit company"
                disabled={!crmContactFormData.company_id}
                onClick={() => {
                  const company = crmCompaniesList.find((c) => c.id === crmContactFormData.company_id);
                  openCrmCompanyEdit(company);
                }}
                sx={{ mt: 1 }}
              >
                <EditIcon />
              </IconButton>
            </Box>
            <TextField
              label="First Name"
              fullWidth
              value={crmContactFormData.first_name}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, first_name: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={crmContactFormData.last_name}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, last_name: e.target.value })}
            />
            <TextField
              label="Title"
              fullWidth
              value={crmContactFormData.title}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, title: e.target.value })}
            />
            <TextField
              label="LinkedIn"
              fullWidth
              value={crmContactFormData.linkedin}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, linkedin: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={crmContactFormData.email}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, email: e.target.value })}
            />
            <TextField
              label="Phone"
              fullWidth
              value={crmContactFormData.phone}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, phone: e.target.value })}
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={4}
              value={crmContactFormData.notes}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, notes: e.target.value })}
            />
            <TextField
              label="Score"
              type="number"
              fullWidth
              value={crmContactFormData.prospect_score}
              onChange={(e) => {
                const val = e.target.value;
                setCrmContactFormData({
                  ...crmContactFormData,
                  prospect_score: val === '' ? -1 : parseInt(val, 10),
                });
              }}
              inputProps={{ min: -1, max: 10, step: 1 }}
              helperText="-1 to 10"
              sx={{ maxWidth: 160 }}
            />
            <TextField
              label="Reasoning"
              fullWidth
              multiline
              rows={4}
              value={crmContactFormData.prospect_reason}
              onChange={(e) => setCrmContactFormData({ ...crmContactFormData, prospect_reason: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={crmContactFormData.status}
                label="Status"
                onChange={(e) => setCrmContactFormData({ ...crmContactFormData, status: e.target.value })}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="email_2">Email 2</MenuItem>
                <MenuItem value="email_3">Email 3</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="reply">Reply</MenuItem>
                <MenuItem value="won">Won</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
                <MenuItem value="wrong">Wrong</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="click">Click</MenuItem>
                <MenuItem value="followup">Followup</MenuItem>
                <MenuItem value="demo">Demo</MenuItem>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="unsubscribe">Unsubscribe</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (crmCompanyDialogOpen) return;
              setCrmContactDialogOpen(false);
            }}
            disabled={savingCrmContact}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={savingCrmContact}
            startIcon={savingCrmContact ? <CircularProgress size={20} color="inherit" /> : null}
            onClick={() => handleSaveCrmContact({
              isCreate: true,
              onSuccess: () => setCrmContactDialogOpen(false),
            })}
          >
            {savingCrmContact ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Config Edit Dialog */}
      <Dialog open={configDialogOpen} onClose={handleCloseConfigDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Config - {editingConfig?.reward_provider?.display_name || editingConfig?.reward_provider?.name || 'Reward Provider'}
        </DialogTitle>
        <DialogContent>
          {configError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {configError}
            </Alert>
          )}
          {editingConfig?.reward_provider?.config_template && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Config Template:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(editingConfig.reward_provider.config_template, null, 2)}
              </Typography>
            </Alert>
          )}
          <TextField
            label="Config JSON"
            fullWidth
            multiline
            rows={15}
            value={configJson}
            onChange={(e) => {
              setConfigJson(e.target.value);
              setConfigError('');
            }}
            error={!!configError}
            helperText={configError || 'Enter valid JSON configuration'}
            sx={{ mt: 2 }}
            InputProps={{
              style: { fontFamily: 'monospace', fontSize: '0.875rem' }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfigDialog}>Cancel</Button>
          <Button onClick={handleSaveConfig} variant="contained" disabled={!!configError}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extra Module Config Edit Dialog */}
      <Dialog open={extraModuleConfigDialogOpen} onClose={handleCloseExtraModuleConfigDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Config - {editingExtraModuleConfig?.extra_module?.name || 'Extra Module'}
        </DialogTitle>
        <DialogContent>
          {extraModuleConfigError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {extraModuleConfigError}
            </Alert>
          )}
          {editingExtraModuleConfig?.extra_module?.config_template && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Config Template:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(editingExtraModuleConfig.extra_module.config_template, null, 2)}
              </Typography>
            </Alert>
          )}
          <TextField
            label="Config JSON"
            fullWidth
            multiline
            rows={15}
            value={extraModuleConfigJson}
            onChange={(e) => {
              setExtraModuleConfigJson(e.target.value);
              setExtraModuleConfigError('');
            }}
            error={!!extraModuleConfigError}
            helperText={extraModuleConfigError || 'Enter valid JSON configuration'}
            sx={{ mt: 2 }}
            InputProps={{
              style: { fontFamily: 'monospace', fontSize: '0.875rem' }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtraModuleConfigDialog}>Cancel</Button>
          <Button onClick={handleSaveExtraModuleConfig} variant="contained" disabled={!!extraModuleConfigError}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
        open={aiVerifyLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, px: 3, textAlign: 'center' }}>
          <CircularProgress color="inherit" size={56} thickness={4} />
          <Typography color="inherit" variant="h6" component="span">
            Verifying prospects…
          </Typography>
          <Typography color="inherit" variant="body2" sx={{ opacity: 0.9, maxWidth: 360 }}>
            Fetching company sites and scoring with AI. This can take a little while for several contacts.
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default Admin; 